/* GRN · Inward — Goods Receipt Notes.
 * List + detail panel + a slide-in "New GRN" drawer modelled on the
 * eShopAid Direct Purchase flow: a single-page form —
 *   Purchase details (invoice header, collapsible)
 *   Products (line items with pricing / tax / batch)
 *   Computed totals
 * Each posted GRN creates inventory lots and IN transactions. */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import supplyService from "../../Services/supplyService";
import { CUREBAY_COLUMNS, CUREBAY_SAMPLE_ROW, parseCurebayRows } from "./grnCurebayImport";
import {
  getResolvedWarehouseCode,
  getCurrentUserName,
  getCurrentUserMobile,
} from "../../Services/warehouseContext";
import {
  cbFmtINR,
  cbFmtNum,
  cbFmtTime12,
  cbFmtDateTime12,
  cbStatusVariant,
  CB_GRN_SERIES,
  CB_PAYMENT_MODES,
} from "./supplyData";
import {
  Button,
  Pill,
  Input,
  Select,
  SearchableSelect,
  Table,
  Tr,
  Td,
  KV,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  TabBar,
  Eyebrow,
  LoadingState,
  ErrorState,
  InlineAlert,
  IconDownload,
  IconPlus,
  IconX,
  IconCheck,
  IconChevRight,
  IconArrowRight,
  IconAlert,
  IconPackage,
  IconReceipt,
  phoneError,
} from "./supplyKit";
import { WarehouseSelect } from "./warehousePicker";
import { useWarehouse } from "../Layout/WarehouseContext";
import { useTaskCenter } from "../Layout/TaskCenterContext";
import { exportToXlsx } from "./Reports/reportUtils";
import rackService from "./Racks/rackService";
import { RackPicker } from "./Racks/RackPicker";

const grnTh = {
  padding: "5px 8px",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  textAlign: "left",
  borderBottom: "1px solid var(--border-default)",
  whiteSpace: "nowrap",
};
const grnTd = { padding: "6px 8px", borderBottom: "1px solid var(--border-subtle)", verticalAlign: "middle" };

/* "2026-10-07" or "2026-10-07T17:13:29" -> "07 Oct 26", same style as the date filter. */
const fmtGrnDate = (v) => {
  const m = moment(String(v || "").slice(0, 10), "YYYY-MM-DD", true);
  return m.isValid() ? m.format("DD MMM YY") : "";
};

/* Rows fetched per request when exporting the whole filtered list. */
const GRN_EXPORT_PAGE_SIZE = 200;

const fieldInput = {
  width: "100%",
  height: 30,
  padding: "0 8px",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  background: "var(--bg-panel)",
  color: "var(--fg-default)",
  outline: "none",
};
const fieldMono = { ...fieldInput, fontFamily: "var(--font-mono)", textAlign: "right" };
const fieldLabel = {
  display: "block",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  marginBottom: 4,
};

/* ---------- helpers ---------- */
const num = (v) => Number(v) || 0;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
/* ISO date -> MM/YY (e.g. 2026-09-30 -> 09/26). */
const toMMYY = (iso) => {
  if (!iso) return "";
  const [y, m] = String(iso).split("-");
  return m && y ? `${m}/${String(y).slice(-2)}` : iso;
};

/* Normalise an expiry cell coming out of a bulk-import sheet into MM/YY.
   Suppliers' sheets usually format the expiry column as a real *date*, so
   SheetJS hands back an Excel serial number (47150) or a JS Date rather than
   the "02/29" the cell displays. Passing the serial straight through produced
   a backend failure ("Text '47150' could not be parsed at index 0"), so coerce
   date-ish cells here; plain strings pass through untouched. */
const expiryFromCell = (v) => {
  if (v == null || v === "") return "";
  if (v instanceof Date) {
    return `${String(v.getMonth() + 1).padStart(2, "0")}/${String(v.getFullYear()).slice(-2)}`;
  }
  if (typeof v === "number" && Number.isFinite(v)) {
    const d = XLSX.SSF?.parse_date_code(v);
    return d && d.m && d.y ? `${String(d.m).padStart(2, "0")}/${String(d.y).slice(-2)}` : String(v);
  }
  return String(v).trim();
};

/* Auto-format expiry as the operator types: digits only, with the "/" inserted
   after the month so "0229" becomes "02/29" without anyone typing the slash.
   A lone digit 2-9 can only be month "0N", so pad it and advance. `prev` lets
   the formatter tell typing from deleting — without it, backspacing over the
   auto-inserted "/" would immediately re-add it and trap the caret. */
const formatExpiryInput = (raw, prev = "") => {
  const s = String(raw ?? "");
  const deleting = s.length < String(prev).length;
  const digits = s.replace(/\D/g, "").slice(0, 4);
  if (!digits) return "";
  if (digits.length === 1) return !deleting && digits >= "2" ? `0${digits}/` : digits;
  const mm = digits.slice(0, 2);
  const yy = digits.slice(2);
  if (yy) return `${mm}/${yy}`;
  return deleting ? mm : `${mm}/`;
};

/* Expiry is MM/YY (also tolerates MM/YYYY from older bulk files); must be a
   real month and not already past. */
const EXPIRY_RE = /^(0[1-9]|1[0-2])\/(\d{2}|\d{4})$/;
const isValidExpiry = (v) => {
  const s = String(v || "").trim();
  if (!EXPIRY_RE.test(s)) return false;
  const [mm, yr] = s.split("/");
  const year = yr.length === 2 ? 2000 + Number(yr) : Number(yr);
  const month = Number(mm);
  const now = new Date();
  // Allow the current month and later; block already-expired stock.
  return year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);
};
/* Statutory GST slabs (%). 0 covers exempt/nil-rated items. A GRN line must
   carry one of these — an arbitrary rate would flow into the stored tax and
   the tax invoice. */
const GST_SLABS = [0, 5, 12, 18, 28];
const isValidGst = (v) => {
  if (v === "" || v == null) return false;
  const n = Number(v);
  return Number.isFinite(n) && GST_SLABS.includes(n);
};
/* HSN is mandatory and is exactly 6 or 8 digits — the two lengths GST actually
   defines for what we receive. Digits only: no spaces, dots or dashes.

   The length is checked exactly rather than as a minimum. A minimum waves through
   the malformed codes the medicine master is full of — an 8-digit code with a
   couple of digits run onto the end ("30049099" arriving as "3004909912" or
   "30049099912") — and those are the ones worth catching, because nothing about
   them looks wrong in a table.

   It is enforced rather than defaulted: the value flows straight onto the tax
   invoice's HSN column and into the GST summary, so a bad one has to be answered
   by the operator receiving the stock, not filled in with a stand-in. */
const HSN_DIGITS = [6, 8];
const HSN_RULE_TEXT = "6 or 8 digits";
const isValidHsn = (v) => {
  if (v === "" || v == null) return false;
  const s = String(v).trim();
  return /^\d+$/.test(s) && HSN_DIGITS.includes(s.length);
};
/* Unit pack = units per strip, sent to the backend as `unitsPerStrip` and used to
   resolve loose-unit stock. Mandatory on every line and a whole number ≥ 1 (1 =
   the item is sold as a single unit); an empty or fractional pack would leave the
   backend guessing off the packing string. */
const isValidUnitPack = (v) => {
  if (v === "" || v == null) return false;
  const s = String(v).trim();
  return /^\d+$/.test(s) && Number(s) >= 1;
};
/* Normalise a packing value from a bulk sheet / medicine master into that integer.
   Pack strings carry at most two numbers and the units-per-strip is the last one in
   both conventions in use — `15'S` → 15, `1*15` → 15, `10 x 10` → 10. Anything with
   no digits yields "" so the line shows as invalid instead of a wrong pack size. */
const toUnitPack = (v) => {
  const runs = String(v ?? "").match(/\d+/g);
  if (!runs) return "";
  const n = Number(runs[runs.length - 1]);
  return n >= 1 ? String(n) : "";
};
/* Per-line price / tax math. */
const lineCalc = (l) => {
  const qty = num(l.quantity);
  const pp = num(l.purchasePrice);
  /* Sale price is not captured on a GRN — the pharmacy sells at MRP, so MRP is
     both the displayed sale price and the basis for the margin. */
  const sp = num(l.mrp);
  const gross = qty * pp;
  /* Supplier scheme discount — a flat amount off the gross, taken BEFORE the
     trade discount. The two cascade rather than adding up, matching the backend
     engine: taxable = gross − scheme − discount(on the post-scheme base).
     Capped at the gross so a mistyped scheme can't drive the base negative. */
  const schemeAmt = Math.min(gross, Math.max(0, num(l.schemeDiscount)));
  const schemeBase = gross - schemeAmt;
  /* Mirror the same amount-or-percent choice the pricing payload makes
     (computePurchasePricing -> lineDiscount): a flat amount wins when present,
     otherwise the percentage applies. Reading only `discountAmount` made every
     percentage-based line — all Curebay imports — show a zero discount whenever
     these fallback numbers were on screen. */
  const discAmt =
    l.discountAmount !== "" && l.discountAmount != null && num(l.discountAmount) > 0
      ? num(l.discountAmount)
      : round2((schemeBase * num(l.discountPct)) / 100);
  const taxable = Math.max(0, schemeBase - discAmt);
  const taxPct = num(l.taxPct);
  const sgst = (taxable * taxPct) / 200;
  const cgst = (taxable * taxPct) / 200;
  const tax = sgst + cgst;
  const margin = pp > 0 ? ((sp - pp) / pp) * 100 : 0;
  return {
    qty,
    pp,
    sp,
    gross,
    schemeAmt,
    discAmt,
    /* Every reduction on the line — what the totals row shows as "discount". */
    totalDiscAmt: schemeAmt + discAmt,
    taxable,
    taxPct,
    sgst,
    cgst,
    tax,
    amount: taxable + tax,
    margin,
  };
};

/* Margin % on MRP, as the purchase team works it out on the supplier invoice:
     landed cost per strip = taxable (after scheme + trade discount) x (1 + GST %)
                             / (billed + free strips)
     margin %              = (MRP - landed cost) / MRP
   MRP is GST-inclusive, so GST goes into the cost to compare like with like.
   Free strips carry no cost but are sold at MRP, so they spread the cost — the
   same basis as the GRN report (GrnReportRow.marginPercentage). Cash / marketing
   discounts are already inside "Product Discount" on the Curebay file, and the
   bill-level discount is not a line figure, so neither is applied here.
   `taxable` is the backend-priced value when available, else lineCalc's.
   Returns null when there is no MRP or nothing received. */
const purchaseMarginPct = (l, taxable) => {
  const mrp = num(l.mrp);
  const units = num(l.quantity) + num(l.free);
  if (!(mrp > 0) || !(units > 0)) return null;
  const landedPerUnit = (num(taxable) * (1 + num(l.taxPct) / 100)) / units;
  return ((mrp - landedPerUnit) / mrp) * 100;
};

/* Cut to 2 decimals without rounding (23.9956 -> "23.99"), as the purchase team
   asked for the margin. The tiny nudge keeps float noise from cutting a true
   24.00 (stored as 23.9999999…) down to 23.99. */
const trunc2 = (n) => (Math.trunc(n * 100 + Math.sign(n) * 1e-9) / 100).toFixed(2);

const Grn = () => {
  const location = useLocation();
  const initialMode = location.state?.mode; // 'new' | 'upload' | undefined

  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);
  /* Server-side pagination: the list is fetched one page at a time, so `page`
     and `pageSize` go to the API rather than slicing an already-truncated array. */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showNew, setShowNew] = useState(initialMode === "new" || initialMode === "upload");
  const [editingGrn, setEditingGrn] = useState(null);
  /* Seed for the next "New GRN" drawer — captured once from the PO "Receive"
     nav state so it prefills the PO number. A manual "New GRN" clears it, so it
     never lingers onto a later, unrelated open. */
  const [grnSeed, setGrnSeed] = useState(() => location.state?.fromPO || null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [grns, setGrns] = useState([]);
  const [grnLines, setGrnLines] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [medicines, setMedicines] = useState([]);

  /* The store in scope, chosen once in the topbar — see Layout/WarehouseContext. */
  const { code: wh } = useWarehouse();
  const [filters, setFilters] = useState(() => ({
    q: "",
    supplierId: "",
    /* Dates are an optional filter — empty by default so all GRNs show (newest first). */
    fromDate: "",
    toDate: "",
    /* Sort order on entry time, then GRN #: desc (newest first) | asc (oldest first). */
    sortDir: "desc",
  }));
  const setF = (key, val) => setFilters((f) => ({ ...f, [key]: val }));
  /* Debounced copy of `filters.q`, sent to the API as `search`. */
  const [searchTerm, setSearchTerm] = useState("");

  /* Static reference data — loaded once per reload. */
  useEffect(() => {
    let alive = true;
    Promise.all([
      supplyService.getSuppliers(),
      supplyService.getWarehouses(),
      supplyService.getMedicines(),
    ])
      .then(([s, w, m]) => {
        if (!alive) return;
        setSuppliers(s || []);
        setWarehouses(w || []);
        setMedicines(m || []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load reference data.");
      });
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  /* GRN detail lines — fetched when the user selects a row. */
  useEffect(() => {
    if (selected == null) {
      setGrnLines([]);
      return undefined;
    }
    let alive = true;
    supplyService
      .getGrnLines(selected)
      .then((gl) => {
        if (!alive) return;
        setGrnLines(gl || []);
      })
      .catch(() => {
        if (!alive) return;
        setGrnLines([]);
      });
    return () => {
      alive = false;
    };
  }, [selected]);

  /* Debounce the search box so a filter request isn't fired per keystroke. */
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(filters.q.trim()), 400);
    return () => clearTimeout(t);
  }, [filters.q]);

  /* The filters as sent to the API — shared by the list and the export. A date
     range only applies once both ends are picked: the first click of the range
     picker leaves `toDate` empty, and filtering "from that day on" for a moment
     just costs an extra request. Sorted by entry time (then GRN #) server-side. */
  const listParams = useMemo(() => {
    const params = { sort: `createdAt,${filters.sortDir}` };
    if (tab !== "all") params.status = tab.toUpperCase();
    if (searchTerm) params.search = searchTerm;
    if (filters.supplierId) params.supplierId = Number(filters.supplierId);
    if (wh) params.warehouseId = wh;
    if (filters.fromDate && filters.toDate) {
      params.fromDate = filters.fromDate;
      params.toDate = filters.toDate;
    }
    return params;
  }, [tab, searchTerm, filters.supplierId, filters.fromDate, filters.toDate, filters.sortDir, wh]);
  const listKey = JSON.stringify([listParams, pageSize]);
  const lastListKey = useRef(listKey);

  /* GRN list — one page at a time. Every filter (store, supplier, dates, status
     tab, free text) is applied server-side, so what the footer counts is the
     whole matching set and not just the rows that happened to be fetched. */
  useEffect(() => {
    /* A filter change goes back to the first page — page 4 of "all" is not page 4
       of "drafts". When already past page 0, resetting the page re-runs this
       effect, so skip this run rather than fetch the old page number once. */
    if (lastListKey.current !== listKey) {
      lastListKey.current = listKey;
      if (page !== 0) {
        setPage(0);
        return undefined;
      }
    }
    let alive = true;
    setLoading(true);
    setError(null);
    supplyService
      .getGrns({ ...listParams, page, size: pageSize })
      .then((res) => {
        if (!alive) return;
        const rows = res.content || [];
        setGrns(rows);
        setTotalRows(res.totalElements || 0);
        setTotalPages(res.totalPages || 0);
        /* Open the row most likely to be worked on: one still being received,
           else the first on the page. */
        setSelected(rows.find((x) => x.status === "receiving")?.id || rows[0]?.id || null);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load GRNs.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
    // listParams and pageSize are covered by listKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, listKey, page]);

  const supById = (id) => suppliers.find((s) => s.id === id);

  /* The fetched page is already filtered and sliced by the server. */
  const pagedRows = grns;
  const selectedGrn = grns.find((g) => g.id === selected);

  /* Export every GRN matching the current filters, tab, search and sort — not
     just the page on screen. Runs in the task center (topbar bell) so it carries
     on if the user navigates away. */
  const { runTask } = useTaskCenter();
  const [exporting, setExporting] = useState(false);
  const startExport = () => {
    if (exporting || totalRows === 0) return;
    setExporting(true);
    const params = listParams;
    const supName = (g) => supById(g.supplier)?.name || g.supplierName || "";
    const fileName = `grn-${wh || "all"}-${moment().format("YYYY-MM-DD")}.xlsx`;
    runTask({
      title: "GRN list · Excel",
      run: async (onProgress) => {
        const rows = [];
        for (let p = 0, pages = 1; p < pages; p++) {
          onProgress(`Fetching page ${p + 1}${pages > 1 ? ` of ${pages}` : ""}…`);
          const res = await supplyService.getGrns({ ...params, page: p, size: GRN_EXPORT_PAGE_SIZE });
          pages = res.totalPages || 0;
          rows.push(...(res.content || []));
        }
        const save = () =>
          exportToXlsx(
            fileName,
            ["GRN #", "Entry date", "Entry time", "Supplier", "Supplier invoice", "Bill date",
              "PO number", "Warehouse", "Items", "Units", "Value", "Status"],
            rows.map((g) => [
              g.id,
              fmtGrnDate(g.createdAt),
              cbFmtTime12(g.createdAt, { seconds: true }),
              supName(g),
              g.supplierInv || "",
              fmtGrnDate(g.date),
              g.poNumber || "",
              g.wh || "",
              Number(g.items) || 0,
              Number(g.units) || 0,
              Number(g.value) || 0,
              g.status,
            ]),
            "GRNs"
          );
        save();
        return {
          detail: `Downloaded · ${cbFmtNum(rows.length)} rows`,
          action: { label: "Download again", run: save },
        };
      },
    });
    toast.info("Exporting GRNs — track progress in notifications.");
    setTimeout(() => setExporting(false), 1200);
  };

  const startRow = totalRows === 0 ? 0 : page * pageSize + 1;
  const endRow = Math.min(totalRows, page * pageSize + pagedRows.length);

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="GRN · Goods Receipt Notes"
        sub="Inward receipts from suppliers · each posted GRN creates inventory lots and IN transactions"
        actions={
          <>
            <Button
              icon={<IconDownload size={14} />}
              onClick={startExport}
              disabled={exporting || loading || totalRows === 0}
              title={totalRows === 0 ? "Nothing to export" : `Export ${cbFmtNum(totalRows)} GRNs to Excel`}
            >
              Export
            </Button>
            <Button
              variant="primary"
              icon={<IconPlus size={14} />}
              onClick={() => {
                setGrnSeed(null); // manual open — not tied to a PO
                setShowNew(true);
              }}
            >
              New GRN
            </Button>
          </>
        }
      />

      <TabBar
        value={tab}
        onChange={(t) => {
          setTab(t);
          setSelected(null);
        }}
        /* Only the open tab carries a count: status is a server-side filter now,
           so the other tabs' totals are not known without four more requests,
           and a count taken from the current page would understate them. */
        tabs={[
          { id: "all", label: "All" },
          { id: "draft", label: "Drafts" },
          { id: "receiving", label: "Receiving", alert: "amber" },
          { id: "posted", label: "Posted" },
          { id: "cancelled", label: "Cancelled" },
        ].map((t) => (t.id === tab ? { ...t, count: totalRows } : t))}
      />

      <ToolbarBar>
        <Input
          placeholder="GRN / supplier invoice / supplier…"
          style={{ width: 280, maxWidth: "100%" }}
          value={filters.q}
          onChange={(e) => setF("q", e.target.value)}
        />
        {/* Scoped to the warehouse filter, like the drawer's picker — offering a
            supplier from another warehouse can only ever return an empty list. */}
        <SearchableSelect
          value={filters.supplierId}
          onChange={(v) => setF("supplierId", v)}
          options={[
            { value: "", label: "All suppliers" },
            ...suppliers
              .filter(
                (s) =>
                  !wh ||
                  !s.warehouseId ||
                  String(s.warehouseId) === String(wh)
              )
              .map((s) => ({ value: String(s.id), label: s.name })),
          ]}
          placeholder="All suppliers"
          style={{ minWidth: 200 }}
          aria-label="Supplier"
        />
        <DateRangeFilter
          from={filters.fromDate}
          to={filters.toDate}
          onFromChange={(v) => setF("fromDate", v)}
          onToChange={(v) => setF("toDate", v)}
        />
        <Select
          value={filters.sortDir}
          onChange={(e) => setF("sortDir", e.target.value)}
          title="Sort by entry time"
        >
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </Select>
        <Button
          onClick={() => {
            setFilters({
              q: "",
              supplierId: "",
              fromDate: "",
              toDate: "",
              sortDir: "desc",
            });
          }}
        >
          Reset
        </Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(totalRows)} GRN{totalRows !== 1 ? "s" : ""}
        </div>
      </ToolbarBar>

      {loading ? (
        <LoadingState label="Loading GRNs…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <div
          className={selectedGrn ? "grid grid-cols-1 lg:grid-cols-[1fr_440px]" : "grid grid-cols-1"}
          style={{
            gap: 12,
            alignItems: "start",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
          <Table
            columns={[
              { label: "GRN #" },
              { label: "Date & time" },
              { label: "Supplier" },
              { label: "Sup. invoice" },
              { label: "Wh" },
              { label: "Items", align: "right" },
              { label: "Units", align: "right" },
              { label: "Value", align: "right" },
              { label: "Status" },
              { label: "", width: 24 },
            ]}
          >
            {pagedRows.map((g) => {
              const sup = supById(g.supplier);
              return (
                <Tr
                  key={g.id}
                  selected={selected === g.id}
                  onClick={() => setSelected(selected === g.id ? null : g.id)}
                >
                  <Td mono style={{ fontWeight: 600 }}>
                    {g.id}
                  </Td>
                  {/* When the GRN was entered — the order the list is sorted in. */}
                  <Td mono title={g.createdAt ? `Created ${cbFmtDateTime12(g.createdAt)}` : undefined}>
                    <div>{fmtGrnDate(g.createdAt) || fmtGrnDate(g.date)}</div>
                    {cbFmtTime12(g.createdAt) && (
                      <div style={{ fontSize: 10, color: "var(--fg-muted)" }}>
                        {cbFmtTime12(g.createdAt, { seconds: true })}
                      </div>
                    )}
                  </Td>
                  <Td>{sup?.name || g.supplierName}</Td>
                  <Td mono style={{ color: "var(--fg-muted)" }}>
                    <div>{g.supplierInv}</div>
                    {g.date && (
                      <div style={{ fontSize: 10 }} title="Supplier bill date">
                        Bill {fmtGrnDate(g.date)}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <Pill variant="neutral">{g.wh}</Pill>
                  </Td>
                  <Td mono right>
                    {g.items}
                  </Td>
                  <Td mono right>
                    {cbFmtNum(g.units)}
                  </Td>
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtINR(g.value)}
                  </Td>
                  <Td>
                    <Pill variant={cbStatusVariant(g.status)} dot>
                      {g.status}
                    </Pill>
                    {g.slaWarn && (
                      <span style={{ marginLeft: 6, color: "var(--amber-700)" }} title="Shortage detected">
                        <IconAlert size={12} style={{ verticalAlign: "-2px" }} />
                      </span>
                    )}
                  </Td>
                  <Td>
                    <IconChevRight size={12} style={{ color: "var(--fg-muted)" }} />
                  </Td>
                </Tr>
              );
            })}
          </Table>

          <ToolbarBar>
            <Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {startRow}–{endRow} of {cbFmtNum(totalRows)}
              </span>
              <Button size="sm" disabled={page <= 0} onClick={() => setPage(Math.max(0, page - 1))}>
                ← Prev
              </Button>
              <Button
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next →
              </Button>
            </div>
          </ToolbarBar>
          </div>

          {selectedGrn && (
            <GrnDetail
              grn={selectedGrn}
              supplier={supById(selectedGrn.supplier)}
              lines={grnLines}
              medicines={medicines}
              onClose={() => setSelected(null)}
              onChanged={() => {
                setReloadKey((k) => k + 1);
                setSelected(null);
              }}
              onEdit={() => {
                setEditingGrn({ grn: selectedGrn, lines: grnLines });
                setShowNew(true);
              }}
            />
          )}
        </div>
      )}

      {showNew && (
        <GrnNewDrawer
          suppliers={suppliers}
          warehouses={warehouses}
          medicines={medicines}
          editing={editingGrn}
          fromPO={editingGrn ? null : grnSeed}
          onClose={() => {
            setShowNew(false);
            setEditingGrn(null);
            setGrnSeed(null);
          }}
          onSaved={() => {
            setReloadKey((k) => k + 1);
            setShowNew(false);
            setEditingGrn(null);
            setGrnSeed(null);
          }}
        />
      )}
    </div>
  );
};

/* ---------------- Detail panel ---------------- */
const GrnDetail = ({ grn, supplier, lines, medicines, onClose, onChanged, onEdit }) => {
  const hasShortage = lines.some((l) => l.short > 0);
  const medById = (id) => medicines.find((m) => m.id === id);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  /* Reverse-map the list-row header + detail lines back into the drawer's payload shape
     so PATCH /v1/purchases/{id} can send the existing data with a different status. */
  const buildPayload = (status) => ({
    header: {
      invoiceNumber: grn.supplierInv,
      supplier: grn.supplier,
      store: grn.warehouseId,
      poNumber: grn.poNumber,
      purchaseOrderId: grn.purchaseOrderId,
      invoiceDate: grn.date,
      paymentDate: grn.receivedDate || grn.date,
      remarks: grn.note || "NA",
    },
    lines,
    status,
  });

  const runWith = async (label, fn) => {
    setBusy(true);
    try {
      await fn();
      toast.success(`${label} — GRN #${grn.id}.`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || `Could not ${label.toLowerCase()}.`);
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = () => onEdit?.();
  const handleReject = () => {
    if (!window.confirm(`Reject GRN ${grn.id}? Stock will not be created.`)) return;
    runWith("Rejected", () => supplyService.updateGrn(grn.id, buildPayload("CANCELLED")));
  };
  const handleMoveToReceiving = () =>
    runWith("Moved to receiving", () => supplyService.updateGrn(grn.id, buildPayload("RECEIVING")));
  /* This button posts a saved GRN without opening the editor, so it never ran the
     editor's HSN check — bad codes reached prod lots this way. Same rule here
     (IMS refuses the post too); the operator fixes them through Edit. */
  const badHsnLines = lines
    .map((l, i) => ({ l, n: i + 1 }))
    .filter(({ l }) => !isValidHsn(l.hsnCode));
  const badHsnText = badHsnLines
    .slice(0, 5)
    .map(({ l, n }) => {
      const name = medById(l.medId)?.name || l.medicineName || "";
      return `line ${n}${name ? ` (${name})` : ""}`;
    })
    .join(", ") + (badHsnLines.length > 5 ? ` and ${badHsnLines.length - 5} more` : "");
  const handleFinalize = () => {
    if (badHsnLines.length) {
      toast.error(`HSN must be ${HSN_RULE_TEXT} before posting. Fix ${badHsnText} through Edit.`);
      return;
    }
    runWith("Posted · inventory created", () => supplyService.postGrn(grn.id));
  };
  const handleViewLedger = () => navigate("/inventory", { state: { tab: "movements", refId: grn.id } });

  return (
    <aside
      className="min-w-0 lg:sticky lg:top-3 lg:max-h-[calc(100vh-120px)]"
      style={{
        border: "1px solid var(--border-default)",
        borderRadius: 6,
        background: "var(--bg-panel)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--border-default)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--fg-strong)" }}>
              {grn.id}
            </span>
            <Pill variant={cbStatusVariant(grn.status)} dot>
              {grn.status}
            </Pill>
          </div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 4, lineHeight: 1.4 }}>
            {fmtGrnDate(grn.createdAt) || fmtGrnDate(grn.date)} · {supplier?.name || grn.supplierName} · invoice{" "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{grn.supplierInv}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            width: 24,
            height: 24,
            border: 0,
            background: "transparent",
            cursor: "pointer",
            color: "var(--fg-muted)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconX size={14} />
        </button>
      </div>

      {hasShortage && grn.status === "receiving" && (
        <div
          style={{
            padding: "8px 16px",
            background: "var(--amber-50)",
            color: "var(--amber-700)",
            fontSize: 11,
            borderBottom: "1px solid var(--amber-100)",
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
          }}
        >
          <IconAlert size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>Shortage detected.</strong> Some lines were received short. Confirm with the supplier, or post
            with shortage to lock the lot.
          </span>
        </div>
      )}

      <div style={{ flex: 1, overflow: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <KV label="Warehouse" value={grn.wh} mono />
          <KV label="Supplier GSTIN" value={supplier?.gstin} mono small />
          <KV label="Terms" value={supplier?.terms} />
          <KV label="Created by" value={grn.postedBy || "—"} mono small />
        </div>

        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <Eyebrow>Line items · {grn.items}</Eyebrow>
          </div>
          <div style={{ border: "1px solid var(--border-default)", borderRadius: 4, overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}>
              <thead>
                <tr style={{ background: "var(--bg-subtle)" }}>
                  <th style={grnTh}>Medicine</th>
                  <th style={{ ...grnTh, textAlign: "right", width: 50 }}>Ord</th>
                  <th style={{ ...grnTh, textAlign: "right", width: 56 }}>Rec</th>
                  <th style={{ ...grnTh, textAlign: "right", width: 64 }}>₹</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const med = medById(l.medId);
                  /* Prefer the backend's medicineName (carried on the line)
                     when the master-list lookup misses, so the detail panel
                     never renders a blank medicine row. */
                  const displayName = med?.name || l.medicineName || l.productName || l.medId || "";
                  const displaySku = med?.sku || l.productCode || l.medId || "";
                  return (
                    <tr key={i}>
                      <td style={grnTd}>
                        <div style={{ fontWeight: 600, color: "var(--fg-strong)", fontSize: 11 }}>{displayName}</div>
                        <div
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 10,
                            color: "var(--fg-muted)",
                            marginTop: 2,
                          }}
                        >
                          {displaySku} · B {l.batch} · Exp {l.exp}
                          {l.unitsPerStrip ? ` · ${l.unitsPerStrip} units/strip` : ""}
                        </div>
                      </td>
                      <td style={{ ...grnTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{l.ordered}</td>
                      <td
                        style={{
                          ...grnTd,
                          textAlign: "right",
                          fontFamily: "var(--font-mono)",
                          fontWeight: 600,
                          color: l.short > 0 ? "var(--amber-700)" : "var(--fg-default)",
                        }}
                      >
                        {l.received}
                        {l.short > 0 && (
                          <span style={{ display: "block", fontSize: 9, color: "var(--red-700)", fontWeight: 600 }}>
                            −{l.short} short
                          </span>
                        )}
                      </td>
                      <td style={{ ...grnTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                        {cbFmtINR(l.purchase)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div
          style={{
            padding: "8px 10px",
            background: "var(--bg-subtle)",
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: 12,
          }}
        >
          {(() => {
            const net = num(grn.value);
            const gst = num(grn.gstAmount);
            const disc = num(grn.discountAmount);
            const gross = num(grn.totalAmount);
            /* Taxable = gross − discount; fall back to net − GST for older records. */
            const taxable = gross > 0 ? gross - disc : net - gst;
            const roundOff = net - (taxable + gst);
            const between = { display: "flex", justifyContent: "space-between" };
            const muted = { ...between, color: "var(--fg-muted)" };
            const mono = { fontFamily: "var(--font-mono)" };
            return (
              <>
                <div style={between}>
                  <span>Taxable value</span>
                  <span style={mono}>{cbFmtINR(taxable)}</span>
                </div>
                {disc > 0 && (
                  <div style={muted}>
                    <span>Discount</span>
                    <span style={mono}>−{cbFmtINR(disc)}</span>
                  </div>
                )}
                <div style={muted}>
                  <span>GST (CGST + SGST)</span>
                  <span style={mono}>{cbFmtINR(gst)}</span>
                </div>
                {Math.abs(roundOff) >= 0.01 && (
                  <div style={muted}>
                    <span>Round off</span>
                    <span style={mono}>{cbFmtINR(roundOff)}</span>
                  </div>
                )}
                <div style={{ height: 1, background: "var(--border-subtle)", margin: "2px 0" }} />
                <div style={{ ...between, fontWeight: 700, color: "var(--fg-strong)" }}>
                  <span>Total</span>
                  <span style={mono}>{cbFmtINR(net)}</span>
                </div>
              </>
            );
          })()}
        </div>

        <div
          style={{
            padding: "10px 12px",
            background: "var(--ink-50)",
            borderRadius: 4,
            fontSize: 11,
            color: "var(--ink-700)",
            lineHeight: 1.5,
          }}
        >
          <strong>On posting:</strong> {grn.items} new inventory lot{grn.items !== 1 ? "s" : ""} will be created at{" "}
          {grn.wh}, each writing an{" "}
          <Pill variant="green" style={{ fontSize: 9, height: 14, padding: "0 4px", verticalAlign: "baseline" }}>
            IN
          </Pill>{" "}
          transaction. FIFO order recalculates immediately.
        </div>
      </div>

      {(grn.status === "draft" || grn.status === "receiving") && badHsnLines.length > 0 && (
        <div
          role="alert"
          style={{
            margin: "0 16px 10px",
            padding: "8px 10px",
            background: "var(--red-50)",
            border: "1px solid var(--red-100)",
            borderRadius: 4,
            fontSize: 11,
            color: "var(--red-700)",
            lineHeight: 1.5,
          }}
        >
          HSN must be {HSN_RULE_TEXT} on every line before posting. Fix {badHsnText} through Edit.
        </div>
      )}

      <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border-default)", display: "flex", gap: 8, flexWrap: "wrap" }}>
        {(grn.status === "draft" || grn.status === "receiving") && (
          <Button style={{ flex: 1, justifyContent: "center" }} onClick={handleEdit} disabled={busy}>
            Edit
          </Button>
        )}
        {grn.status === "receiving" && (
          <Button
            variant="danger"
            style={{ flex: 1, justifyContent: "center" }}
            onClick={handleReject}
            disabled={busy}
          >
            Reject
          </Button>
        )}
        {grn.status === "draft" && (
          <Button
            variant="primary"
            style={{ flex: 1.4, justifyContent: "center" }}
            icon={<IconArrowRight size={14} />}
            onClick={handleMoveToReceiving}
            disabled={busy}
          >
            {busy ? "Updating…" : "Move to receiving"}
          </Button>
        )}
        {grn.status === "receiving" && (
          <Button
            variant="primary"
            style={{ flex: 1.4, justifyContent: "center" }}
            icon={<IconCheck size={14} />}
            onClick={handleFinalize}
            disabled={busy || badHsnLines.length > 0}
            title={badHsnLines.length ? `Fix HSN (${HSN_RULE_TEXT}) through Edit first` : undefined}
          >
            {busy ? "Posting…" : "Post · create lots"}
          </Button>
        )}
        {grn.status === "posted" && (
          <Button
            variant="primary"
            style={{ flex: 1, justifyContent: "center" }}
            icon={<IconCheck size={14} />}
            onClick={handleViewLedger}
          >
            View ledger
          </Button>
        )}
      </div>
    </aside>
  );
};

/* ============================================================
   New GRN drawer — single-page Direct Purchase form
   ============================================================ */
/* Max ₹ gap between the supplier's declared invoice total and the computed
   line-items total that is accepted as a round-off (suppliers round to the
   nearest rupee). Anything larger is treated as a genuine mismatch. */
const ROUND_OFF_TOLERANCE = 1;

const blankHeader = (suppliers, warehouses, fromPO) => ({
  /* The store is NEVER defaulted to a list position — only the PO being received
     or the warehouse resolved for the logged-in user may set it. Falling back to
     warehouses[0] silently posted stock into whichever warehouse happened to sort
     first. Left blank, the operator must choose. */
  /* A head receives for any store and must say which, so nothing is preselected
     for them; a PH user is pinned to their own. A GRN seeded from a PO always
     inherits that PO's store, whichever the role. */
  /* The store in scope (getResolvedWarehouseCode returns the topbar selection),
     unless this GRN is receiving a PO, which carries its own store. */
  store: fromPO?.warehouseId || getResolvedWarehouseCode() || "",
  supplier: fromPO?.supplier || suppliers[0]?.id || "",
  /* PO this GRN receives against. `poNumber` is the free-text PO number string;
     `purchaseOrderId` is the PO's numeric id — set only when arriving from a PO's
     "Receive" button. Both prefilled from that nav state; optional otherwise. */
  poNumber: fromPO?.id || "",
  purchaseOrderId: fromPO?.poId ?? null,
  invoiceNumber: "",
  invoiceDate: new Date().toISOString().slice(0, 10),
  paymentDate: new Date().toISOString().slice(0, 10),
  invoiceAmount: "",
  inputTax: "",
  coinAdjustment: "",
  discountPct: "",
  billDiscountAmount: "",
  otherCredits: "",
  otherDebits: "",
  grnSeries: CB_GRN_SERIES[0],
  paymentMode: CB_PAYMENT_MODES[0],
  remarks: "",
  /* The supplier's scanned bill. Held on the header so it rides along in the
     create body and, on edit, is echoed back by toPurchasePayload — a general
     GRN update omitting it would wipe the stored link. */
  invoiceDocumentUrl: "",
});

/* Reverse-map a GET-detail line (medId / exp / ordered / received / purchase / sellingPrice)
   back into the drawer's entry shape so the Line items table can render and edit it. */
const linesFromDetail = (detailLines) =>
  (detailLines || []).map((l) => ({
    medId: l.medId || "",
    productCode: l.productCode || l.medId || "",
    productName: l.medicineName || "",
    batch: l.batch || "",
    quantity: l.received ?? l.ordered ?? "",
    free: "",
    expiry: toMMYY(l.exp),
    discountPct: l.discountPct ?? "",
    discountAmount: "",
    schemeDiscount: l.schemeDiscount ?? "",
    mrp: l.mrp ?? "",
    purchasePrice: l.purchase ?? "",
    /* The stored HSN, not blank — reopening a GRN for edit used to clear it, and
       the outbound mapper reads this field, so a save wrote the blank back. */
    taxCategory: l.hsnCode || "",
    purchaseTax: "",
    taxPct: l.taxPct ?? "",
    /* "Packing" is the printed pack label ("1X15", "200ML"); "Unit pack" is the
       integer units-per-strip derived from it. Both are posted. */
    packing: l.packing || "",
    /* Seed unit pack from what the backend resolved (or the printed packing) so
       an edit re-sends it instead of silently re-inferring. */
    unit: l.unitsPerStrip != null ? String(l.unitsPerStrip) : toUnitPack(l.packing),
    /* The stored put-away rack. Only the id comes back on a purchase item, so the
       code is resolved from the store's rack list by the picker. */
    rackId: l.rackId ?? null,
    rackCode: "",
  }));

/* Reverse-map a list-row GRN into the drawer's header shape for edit mode. */
const headerFromGrn = (grn, warehouses, suppliers) => ({
  store: grn.warehouseId || "",
  supplier: grn.supplier || suppliers[0]?.id || "",
  poNumber: grn.poNumber || "",
  purchaseOrderId: grn.purchaseOrderId ?? null,
  invoiceNumber: grn.supplierInv || "",
  invoiceDate: grn.date || "",
  paymentDate: grn.receivedDate || grn.date || "",
  invoiceAmount: grn.value || "",
  inputTax: "",
  coinAdjustment: "",
  discountPct: "",
  billDiscountAmount: "",
  otherCredits: "",
  otherDebits: "",
  grnSeries: CB_GRN_SERIES[0],
  paymentMode: CB_PAYMENT_MODES[0],
  remarks: grn.note || "",
  invoiceDocumentUrl: grn.invoiceDocumentUrl || "",
});

const GrnNewDrawer = ({ onClose, suppliers, warehouses, medicines, editing, fromPO, onSaved }) => {
  const isEditing = Boolean(editing?.grn?.id);
  /* Single-page GRN: purchase details and products live on one screen. The
     details card collapses once it is filled so the products table — where the
     bulk of the work happens — keeps the viewport. */
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [header, setHeader] = useState(() =>
    isEditing ? headerFromGrn(editing.grn, warehouses, suppliers) : blankHeader(suppliers, warehouses, fromPO)
  );
  const [lines, setLines] = useState(() => (isEditing ? linesFromDetail(editing.lines) : []));
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  /* Backend-authoritative pricing (POST v1/calculations/purchase). The browser
     no longer computes tax/discount money — it sends raw lines and renders what
     the server returns. `priced` holds { lines[], totals, warnings }. */
  const [priced, setPriced] = useState({ lines: [], totals: null, warnings: [] });
  const [pricing, setPricing] = useState(false);
  const [priceErr, setPriceErr] = useState(null);
  const setH = (key, val) => setHeader((h) => ({ ...h, [key]: val }));

  /* Bulk import is driven from the footer, so the drawer owns it. Declared after
     setH — the hook receives it by value, so it must already be initialised. */
  const [importFormat, setImportFormat] = useState("custom");
  const { fileRef, importMsg, clearImportMsg, downloadTemplate, handleBulkFile } = useBulkImport({
    setLines,
    setH,
    medicines,
    format: importFormat,
  });

  /* Suppliers belong to a warehouse (Purchase › Suppliers requires one), so the
     picker offers only the ones for the store being received into — a GRN against
     another warehouse's supplier is not a real purchase.
     Suppliers with no warehouseId are kept: rows created before the field became
     mandatory would otherwise vanish from every store's list.
     `store` empty (nothing picked yet) shows everything rather than nothing, so the
     dropdown is never mysteriously empty. */
  const scopedSuppliers = useMemo(() => {
    if (!header.store) return suppliers;
    /* When editing, the GRN's own supplier stays in the list even if it no longer
       belongs to this store — an existing receipt must not have its supplier
       rewritten because the master changed after the fact. */
    const keepId = isEditing ? String(editing?.grn?.supplier ?? "") : "";
    return suppliers.filter(
      (s) =>
        !s.warehouseId ||
        String(s.warehouseId) === String(header.store) ||
        (keepId && String(s.id) === keepId)
    );
  }, [suppliers, header.store, isEditing, editing]);

  /* Suppliers / warehouses load asynchronously. If the drawer mounts before they
     arrive, blankHeader sets supplier/store to "" — sync the default once each list
     is populated so the header validation actually reflects what the user sees.
     In edit mode the IDs are already filled from the existing GRN, so this is a no-op.
     A supplier already chosen but not served by the selected store is cleared here
     too, so switching store can't leave a mismatched supplier on the GRN. */
  useEffect(() => {
    setHeader((h) => {
      const next = { ...h };
      const stillValid =
        next.supplier && scopedSuppliers.some((s) => String(s.id) === String(next.supplier));
      if (!stillValid) next.supplier = scopedSuppliers[0]?.id || "";
      /* Store is deliberately NOT defaulted from the list — picking warehouses[0]
         meant stock could be received into, or ordered for, a warehouse the
         operator never chose. It stays blank until they select one (or the
         logged-in user's warehouse resolved it in blankHeader). */
      return next;
    });
  }, [scopedSuppliers, warehouses]);

  /* Compare loosely so a string from the <select> matches a numeric id from the API. */
  const supplier = suppliers.find((s) => String(s.id) === String(header.supplier));
  const warehouse = warehouses.find((w) => String(w.id) === String(header.store));

  /* Totals across all line items. A header-level "bill discount %" applies on
     top of the per-line discounts: it reduces the taxable value and, since tax
     is linear in taxable, scales the GST proportionally (GST-correct). */
  const totals = useMemo(() => {
    const qty = lines.reduce((s, l) => s + num(l.quantity), 0);
    /* Post-tax GRN adjustments (not part of the tax base, so applied on top of
       the backend's net amount). */
    const adj =
      num(header.otherDebits) - num(header.otherCredits) + num(header.coinAdjustment);
    const t = priced.totals;
    if (t) {
      const tax = round2((t.cgstAmount || 0) + (t.sgstAmount || 0) + (t.igstAmount || 0));
      const amount = round2(t.netAmount);
      const computed = round2(amount + adj);
      return {
        items: lines.length,
        qty,
        gross: round2(t.grossAmount),
        goodsValue: round2(t.taxableAmount),
        /* Every reduction on the bill: scheme + trade + bill discount, so
           gross − discount is exactly the taxable base. The backend also breaks
           it out as schemeDiscount / tradeDiscount if that's ever wanted here. */
        discount: round2(t.totalDiscount),
        taxable: round2(t.taxableAmount),
        tax,
        amount,
        computed,
        difference: round2(num(header.invoiceAmount) - computed),
      };
    }
    /* Transitional fallback while the pricing call is in flight / unavailable.
       Mirrors the backend engine; remove once server pricing is verified. */
    let gross = 0,
      goodsValue = 0,
      lineTax = 0,
      lineDiscount = 0;
    lines.forEach((l) => {
      const c = lineCalc(l);
      gross += c.gross;
      goodsValue += c.taxable;
      lineTax += c.tax;
      /* Scheme + trade, matching the backend's totalDiscount composition. */
      lineDiscount += c.totalDiscAmt;
    });
    const billDiscount = Math.min(
      goodsValue,
      header.billDiscountAmount !== "" && header.billDiscountAmount != null
        ? num(header.billDiscountAmount)
        : round2((goodsValue * num(header.discountPct)) / 100)
    );
    const factor = goodsValue > 0 ? (goodsValue - billDiscount) / goodsValue : 1;
    const taxable = round2(goodsValue - billDiscount);
    const tax = round2(lineTax * factor);
    const amount = round2(taxable + tax);
    const computed = round2(amount + adj);
    return {
      items: lines.length,
      qty,
      gross,
      goodsValue,
      discount: round2(lineDiscount + billDiscount),
      taxable,
      tax,
      amount,
      computed,
      difference: round2(num(header.invoiceAmount) - computed),
    };
  }, [
    priced,
    lines,
    header.discountPct,
    header.billDiscountAmount,
    header.otherDebits,
    header.otherCredits,
    header.coinAdjustment,
    header.invoiceAmount,
  ]);

  /* Per-line server pricing, keyed by the ref we send (the line index). */
  const pricedByRef = useMemo(() => {
    const m = {};
    (priced.lines || []).forEach((pl) => {
      m[String(pl.ref)] = pl;
    });
    return m;
  }, [priced]);

  /* The pricing request body, as a string, built from only the fields the engine
     reads. The effect below keys off this rather than `lines`, so editing a
     non-money field — a rack, a product name — no longer fires a repricing call
     (and, because the effect never touches `lines`, the dependency list stays
     honest). */
  const pricingPayload = useMemo(
    () =>
      JSON.stringify(
        lines.map((l, i) => ({
          ref: String(i),
          medId: l.medId,
          productCode: l.productCode,
          taxCategory: l.taxCategory,
          quantity: l.quantity,
          purchasePrice: l.purchasePrice,
          mrp: l.mrp,
          /* Flat ₹ scheme; the engine takes it off the gross before the trade
             discount below. */
          schemeDiscount: l.schemeDiscount,
          taxPct: l.taxPct,
          expiry: l.expiry,
          lineDiscount:
            l.discountAmount !== "" && l.discountAmount != null && num(l.discountAmount) > 0
              ? { type: "AMT", value: num(l.discountAmount) }
              : { type: "PCT", value: num(l.discountPct) },
        }))
      ),
    [lines]
  );

  /* Debounced authoritative pricing. Sends raw line inputs to
     POST v1/calculations/purchase and stores the returned money. Runs on any
     change to the priced line inputs, the discounts, or the supplier/warehouse
     (state affects the GST intra/inter split). */
  useEffect(() => {
    const payloadLines = JSON.parse(pricingPayload);
    if (!payloadLines.length) {
      setPriced({ lines: [], totals: null, warnings: [] });
      setPriceErr(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      setPricing(true);
      setPriceErr(null);
      supplyService
        .computePurchasePricing({
          supplierState: supplier?.state,
          partyState: warehouse?.state,
          supplierId: header.supplier,
          warehouseId: header.store,
          billDiscount:
            header.billDiscountAmount !== "" &&
            header.billDiscountAmount != null &&
            num(header.billDiscountAmount) > 0
              ? { type: "AMT", value: num(header.billDiscountAmount) }
              : { type: "PCT", value: num(header.discountPct) },
          lines: payloadLines,
        })
        .then((res) => {
          if (cancelled) return;
          setPriced({
            lines: res?.lines || [],
            totals: res?.totals || null,
            warnings: res?.warnings || [],
          });
          setPricing(false);
        })
        .catch((e) => {
          if (cancelled) return;
          setPriceErr(
            e?.response?.data?.message || e?.message || "Could not price the GRN lines."
          );
          setPricing(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    pricingPayload,
    header.discountPct,
    header.billDiscountAmount,
    header.supplier,
    header.store,
    supplier,
    warehouse,
  ]);

  /* `store` is required now that it is never defaulted — without it a GRN would
     post with an empty warehouseId and the lots would land nowhere. */
  const headerValid =
    header.store &&
    header.supplier &&
    header.invoiceNumber.trim() &&
    num(header.invoiceAmount) > 0 &&
    header.invoiceDate;
  /* The supplier-declared invoice total (header.invoiceAmount) is reconciled
     against the amount computed from the line items + adjustments before the
     GRN can be posted. Suppliers commonly round their invoice total to the
     nearest rupee, so a small difference is treated as a permitted round-off
     (within ROUND_OFF_TOLERANCE) rather than a blocking mismatch. Draft saves
     are always allowed so operators can stash partial entries. */
  const invoiceAmount = num(header.invoiceAmount);
  const roundOff = totals.difference; // invoiceAmount - computed
  const isExactMatch = invoiceAmount > 0 && Math.abs(roundOff) < 0.01;
  const valuesMatch =
    invoiceAmount > 0 && Math.abs(roundOff) < ROUND_OFF_TOLERANCE;
  const canSaveDraft = headerValid && lines.length > 0;
  /* Every line must carry a valid expiry (MM/YY, not past) before posting. */
  const allExpiryValid = lines.every((l) => isValidExpiry(l.expiry));
  /* Every line must carry a valid GST slab before posting — catches bulk-imported
     rows, which bypass the Add-product form's GST validation. */
  const allGstValid = lines.every((l) => isValidGst(l.taxPct));
  /* Same for HSN — it lands on the tax invoice and in GSTR-1, and bulk-imported
     or master-prefilled rows never passed through the Add-product form. */
  const allHsnValid = lines.every((l) => isValidHsn(l.taxCategory));
  /* Names the failing lines in a posting alert ("line 3 (Dolo 650)"), so the
     operator doesn't have to scan the whole table to find them. */
  const badLinesText = (isOk) => {
    const bad = lines
      .map((l, i) => ({ l, n: i + 1 }))
      .filter(({ l }) => !isOk(l))
      .map(({ l, n }) => `line ${n}${l.productName ? ` (${l.productName})` : ""}`);
    const shown = bad.slice(0, 5).join(", ");
    return bad.length > 5 ? `${shown} and ${bad.length - 5} more` : shown;
  };
  /* Unit pack is mandatory on every line — same reason as GST: bulk-imported rows
     bypass the Add-product form, so re-check here before posting. */
  const allUnitPackValid = lines.every((l) => isValidUnitPack(l.unit));
  /* Every line must resolve to a product. A Curebay row with no CBDrugCode is
     imported unmapped on purpose, but an empty medId reaches the backend as an
     unknown product, so posting waits until the operator has picked one. */
  const allMapped = lines.every((l) => String(l.medId || l.productCode || "").trim());
  /* Posting requires authoritative backend pricing — never post on the
     transitional fallback numbers or while a price call is pending/failed. */
  const canPost =
    canSaveDraft &&
    valuesMatch &&
    allExpiryValid &&
    allGstValid &&
    allHsnValid &&
    allUnitPackValid &&
    allMapped &&
    !pricing &&
    !priceErr &&
    !!priced.totals;

  /* Create flow: POST /v1/purchases (status DRAFT or RECEIVING). Edit flow: PATCH
     /v1/purchases/{id} with the same body shape. Neither call creates inventory —
     a later "Post · create lots" action on the detail panel handles that. */
  const handleSubmit = (status) => {
    setSaving(true);
    setSaveErr(null);
    const call = isEditing
      ? supplyService.updateGrn(editing.grn.id, { header, lines, totals, status })
      : supplyService.saveGrn({ header, lines, totals, status });
    call
      .then(() => {
        setSaving(false);
        if (onSaved) onSaved();
        else onClose();
      })
      .catch((e) => {
        setSaving(false);
        setSaveErr(
          e?.response?.data?.message ||
            e?.message ||
            (status === "DRAFT" ? "Could not save the draft." : "Could not post the GRN.")
        );
      });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          /* Wide enough for the single-line product entry row (~1400px of fields)
             on a full-size monitor; narrower screens scroll that row instead.
             Full width on a phone. */
          background: "var(--bg-canvas)",
          boxShadow: "-12px 0 40px rgba(13,17,27,0.18)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        className="cb-animate-in w-full sm:w-[min(1560px,98vw)]"
      >
        {/* Header bar */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            padding: "12px 20px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              background: "var(--ink-50)",
              color: "var(--ink-700)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconPackage size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg-strong)" }}>
              {isEditing ? `Edit GRN #${editing.grn.id}` : "New GRN · Direct Purchase"}
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
              Each posted line becomes an inventory lot with an IN transaction
            </div>
          </div>
          <HeaderStatus headerValid={headerValid} lineCount={lines.length} />
          <button
            onClick={onClose}
            style={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              color: "var(--fg-muted)",
              width: 28,
              height: 28,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconX size={16} />
          </button>
        </div>

        {/* Body — one page: purchase details, then products, then the computed totals */}
        <div
          style={{ flex: 1, overflow: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}
          className="cb-scroll"
        >
          <PurchaseDetailsSection
            header={header}
            setH={setH}
            supplier={supplier}
            /* Only the selected store's suppliers are offered — see scopedSuppliers. */
            suppliers={scopedSuppliers}
            warehouses={warehouses}
            totals={totals}
            open={detailsOpen}
            onToggle={() => setDetailsOpen((v) => !v)}
            headerValid={headerValid}
            /* Present only on an edit — decides whether the scan uploads straight
               onto the GRN or goes to the unattached route for the create body. */
            grnId={editing?.grn?.id ?? null}
          />
          <LineItemsSection
            lines={lines}
            setLines={setLines}
            header={header}
            supplier={supplier}
            onClearImportMsg={clearImportMsg}
            totals={totals}
            pricing={pricing}
            priceErr={priceErr}
            warnings={priced.warnings}
            pricedByRef={pricedByRef}
          />
          <ComputedSummary totals={totals} />
        </div>

        {!headerValid && (
          <div style={{ padding: "10px 20px 0" }}>
            <InlineAlert>
              Purchase details are incomplete — store, supplier, invoice number, invoice amount and
              invoice date are required before saving. Goods are received into one store, so pick a
              single store in the topbar if “All stores” is selected.
            </InlineAlert>
          </div>
        )}

        {canSaveDraft && !valuesMatch && (
          <div style={{ padding: "10px 20px 0" }}>
            <InlineAlert>
              {invoiceAmount > 0 ? (
                <>
                  Invoice total <strong>{cbFmtINR(invoiceAmount)}</strong> does not match the
                  computed line-items total <strong>{cbFmtINR(totals.computed)}</strong>{" "}
                  (off by <strong>{cbFmtINR(totals.difference)}</strong>). Adjust the line items
                  or the invoice amount before posting.
                </>
              ) : (
                <>Enter the supplier&apos;s invoice amount in the purchase details before posting.</>
              )}
            </InlineAlert>
          </div>
        )}

        {canSaveDraft && valuesMatch && allMapped && !allExpiryValid && (
          <div style={{ padding: "10px 20px 0" }}>
            <InlineAlert>
              Every line needs a valid expiry (MM/YY, not past) before posting. Fix the highlighted lines.
            </InlineAlert>
          </div>
        )}

        {canSaveDraft && valuesMatch && allMapped && allExpiryValid && !allGstValid && (
          <div style={{ padding: "10px 20px 0" }}>
            <InlineAlert>
              Every line needs a valid GST slab (0, 5, 12, 18, 28) before posting. Fix{" "}
              {badLinesText((l) => isValidGst(l.taxPct))} — the GST column is highlighted in red.
            </InlineAlert>
          </div>
        )}

        {canSaveDraft && valuesMatch && !allMapped && (
          <div style={{ padding: "10px 20px 0" }}>
            <InlineAlert>
              {lines.filter((l) => !String(l.medId || l.productCode || "").trim()).length} line
              {lines.filter((l) => !String(l.medId || l.productCode || "").trim()).length !== 1 ? "s" : ""} have
              no product code (shown as “— unmapped —”). Edit each one and pick the product before posting —
              a draft can still be saved.
            </InlineAlert>
          </div>
        )}

        {canSaveDraft && valuesMatch && allMapped && allExpiryValid && allGstValid && !allHsnValid && (
          <div style={{ padding: "10px 20px 0" }}>
            <InlineAlert>
              Every line needs a valid HSN code ({HSN_RULE_TEXT}) before posting. Fix{" "}
              {badLinesText((l) => isValidHsn(l.taxCategory))} — the HSN column is highlighted in red. A line
              prefilled from the medicine master can carry a malformed one.
            </InlineAlert>
          </div>
        )}

        {canSaveDraft &&
          valuesMatch &&
          allMapped &&
          allExpiryValid &&
          allGstValid &&
          allHsnValid &&
          !allUnitPackValid && (
            <div style={{ padding: "10px 20px 0" }}>
              <InlineAlert>
                Every line needs a unit pack (whole number, 1 or more) before posting. Fix the highlighted lines.
              </InlineAlert>
            </div>
          )}

        {importMsg && (
          <div style={{ padding: "10px 20px 0" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "6px 10px",
                borderRadius: 4,
                background: importMsg.tone === "ok" ? "var(--green-50)" : "var(--amber-50)",
                color: importMsg.tone === "ok" ? "var(--green-700)" : "var(--amber-700)",
              }}
            >
              {importMsg.text}
            </div>
          </div>
        )}

        {saveErr && (
          <div style={{ padding: "10px 20px 0" }}>
            <InlineAlert>{saveErr}</InlineAlert>
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            padding: "12px 20px",
            borderTop: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <Button onClick={onClose}>Cancel</Button>
          {/* Bulk import — template + upload live here so the body is all data entry */}
          <select
            value={importFormat}
            onChange={(e) => setImportFormat(e.target.value)}
            title="Which layout the uploaded file uses"
            style={{ ...fieldInput, width: "auto", height: 30, fontSize: 12 }}
          >
            {GRN_IMPORT_FORMATS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
          <Button icon={<IconDownload size={14} />} onClick={downloadTemplate}>
            Download template
          </Button>
          <Button
            icon={<IconDownload size={14} style={{ transform: "rotate(180deg)" }} />}
            onClick={() => fileRef.current?.click()}
          >
            Upload file
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xls,.xlsx,.xlsm,.xlsb,.ods,.txt,.tsv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,*/*"
            style={{ display: "none" }}
            onChange={(e) => {
              handleBulkFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <div style={{ flex: 1 }} />
          <span
            style={{
              fontSize: 11,
              color: !canSaveDraft
                ? "var(--fg-muted)"
                : valuesMatch
                ? "var(--green-700)"
                : "var(--amber-700)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {!headerValid
              ? "Store + supplier + invoice no. + amount + date required"
              : !canSaveDraft
              ? "Add at least one line item"
              : isExactMatch
              ? "Invoice total matches line items"
              : valuesMatch
              ? `Round off ${cbFmtINR(roundOff)} applied`
              : `Off by ${cbFmtINR(totals.difference)} — post blocked`}
          </span>
          <Button disabled={!canSaveDraft || saving} onClick={() => handleSubmit("DRAFT")}>
            {saving ? "Saving…" : "Save draft"}
          </Button>
          <Button
            variant="primary"
            icon={<IconCheck size={14} />}
            disabled={!canPost || saving}
            onClick={() => handleSubmit("RECEIVING")}
          >
            {saving ? "Posting…" : `Post · create ${lines.length || 0} lot${lines.length !== 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* Compact readiness chips in the drawer header — the one-page replacement for the
   old two-step progress dots. */
const HeaderStatus = ({ headerValid, lineCount }) => {
  const chips = [
    { label: "Purchase details", ok: Boolean(headerValid) },
    { label: `Products · ${lineCount}`, ok: lineCount > 0 },
  ];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {chips.map((c) => (
        <span
          key={c.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            borderRadius: 99,
            fontSize: 11,
            fontWeight: 600,
            background: c.ok ? "var(--green-50)" : "var(--bg-sunken)",
            color: c.ok ? "var(--green-700)" : "var(--fg-muted)",
          }}
        >
          {c.ok && <IconCheck size={11} />}
          {c.label}
        </span>
      ))}
    </div>
  );
};

/* ---------------- Purchase details (collapsible) ---------------- */
const Field = ({ label, children, className }) => (
  <div className={className}>
    <label style={fieldLabel}>{label}</label>
    {children}
  </div>
);

/* The supplier's paper bill, scanned. 10MB is the app-wide ceiling; checking it
   here turns a wasted upload and a 413 into an instant message. The backend does
   NOT check content type, so the picker is the only restriction — keep `accept`
   in step with what the viewer can actually render. */
const MAX_DOC_BYTES = 10 * 1024 * 1024;
const DOC_ACCEPT = "image/jpeg,image/png,application/pdf";

const documentError = (e) => {
  const status = e?.response?.status;
  const msg = e?.response?.data?.message;
  if (status === 413) return "File is too large. The maximum upload size is 10MB.";
  if (status === 404) return "This GRN no longer exists.";
  /* 422 is either a cancelled GRN (not retryable) or storage being unreachable
     (retryable) — the backend's message says which, and is safe to show. */
  if (status === 400 || status === 422) return msg || "The invoice copy could not be attached.";
  return msg || e?.message || "Could not attach the invoice copy.";
};

/* Attach / view the scanned supplier bill.

   Two upload routes, picked by whether the GRN exists yet:
     · saved GRN  -> POST /purchases/{id}/document, attached server-side in one
       call (works even once posted, which is when bills usually get scanned).
     · new GRN    -> POST /purchases/documents, which returns a URL we hold on the
       header until the create body carries it.

   Viewing goes through the download endpoint rather than the stored URL: the
   bucket is private, so the raw link 403s in a tab. Before the GRN is saved there
   is no id to download by, so the freshly-picked file is named but not viewable. */
const InvoiceCopyField = ({ grnId, url, fileName, onAttached }) => {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const pick = (file) => {
    if (!file) return;
    setErr(null);
    if (file.size > MAX_DOC_BYTES) {
      setErr("File is too large. The maximum upload size is 10MB.");
      return;
    }
    setBusy(true);
    const req = grnId
      ? supplyService.uploadGrnDocument(grnId, file).then((res) => {
          const data = res?.data || res;
          return data?.invoiceDocumentUrl || "";
        })
      : supplyService
          .uploadGrnDocumentUnattached(file, undefined)
          .then((res) => res?.invoiceDocumentUrl || "");
    req
      .then((next) => {
        if (!next) throw new Error("Upload did not return a document URL.");
        onAttached(next, file.name);
        toast.success("Invoice copy attached.");
      })
      .catch((e) => setErr(documentError(e)))
      .finally(() => {
        setBusy(false);
        if (fileRef.current) fileRef.current.value = "";
      });
  };

  const view = () => {
    if (!grnId) return;
    setErr(null);
    supplyService
      .downloadGrnDocument(grnId)
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, "_blank", "noopener");
        /* Revoked on a delay rather than immediately — the new tab still has to
           fetch it. */
        setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
      })
      .catch((e) => setErr(documentError(e)));
  };

  return (
    <Field label="Invoice copy">
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <input
          ref={fileRef}
          type="file"
          accept={DOC_ACCEPT}
          onChange={(e) => pick(e.target.files?.[0])}
          style={{ display: "none" }}
        />
        <Button size="sm" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? "Uploading…" : url ? "Replace" : "Attach scan"}
        </Button>
        {url && grnId && (
          <Button size="sm" variant="ghost" onClick={view}>
            View
          </Button>
        )}
        <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>
          {url ? fileName || "Attached" : "Not attached"}
        </span>
      </div>
      {err && (
        <div style={{ fontSize: 10, color: "var(--red-700)", marginTop: 3 }}>{err}</div>
      )}
    </Field>
  );
};

const PurchaseDetailsSection = ({
  header,
  setH,
  supplier,
  suppliers,
  warehouses,
  totals,
  open,
  onToggle,
  headerValid,
  grnId,
}) => {
  /* Bill discount % and amount stay in sync via the goods value (known once
     products are added). Editing either fills the other; amount is authoritative. */
  const goods = totals?.goodsValue || 0;
  const onBillPct = (pct) => {
    setH("discountPct", pct);
    setH("billDiscountAmount", goods > 0 ? round2((goods * num(pct)) / 100) : "");
  };
  const onBillAmt = (amt) => {
    setH("billDiscountAmount", amt);
    if (goods > 0 && amt !== "") setH("discountPct", round2((num(amt) / goods) * 100));
  };
  return (
    <div
      style={{
        background: "var(--bg-panel)",
        border: "1px solid var(--border-default)",
        borderRadius: 6,
        padding: "14px 16px",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          border: 0,
          background: "transparent",
          padding: 0,
          marginBottom: open ? 10 : 0,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <Eyebrow>Purchase details</Eyebrow>
        <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {open
            ? ""
            : `${supplier?.name || "No supplier"} · ${header.invoiceNumber || "no invoice no."} · ${cbFmtINR(
                num(header.invoiceAmount)
              )}`}
        </span>
        <span style={{ flex: 1 }} />
        {!open && !headerValid && (
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--red-700)" }}>Incomplete</span>
        )}
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-700)" }}>
          {open ? "Hide" : "Edit"}
        </span>
      </button>
      {open && (
      <div style={{ overflowX: "auto" }} className="cb-scroll">
      {/* Line 1 — store / supplier / invoice identity */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[repeat(9,minmax(120px,1fr))] lg:min-w-[1180px]"
        style={{ gap: 8 }}
      >
        <Field label="Store">
          {/* Searchable: a head receives into any of 10+ similarly-named stores.
              A PH user is locked to their own, so they get a static label. */}
          <WarehouseSelect
            value={header.store}
            onChange={(v) => setH("store", v)}
            warehouses={warehouses}
            noneLabel="— Select store —"
            /* Stock is received into the store you are working in, chosen once in
               the topbar — so this is a label, not a choice. A GRN seeded from a PO
               shows that PO's store. */
            disabled
            width="100%"
          />
        </Field>
        {/* No header "Location" field: storage is decided per batch, not per
            invoice — every line carries its own rack in the products table. */}
        <Field label="GRN series">
          <select value={header.grnSeries} onChange={(e) => setH("grnSeries", e.target.value)} style={fieldInput}>
            {CB_GRN_SERIES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Payment mode">
          <select value={header.paymentMode} onChange={(e) => setH("paymentMode", e.target.value)} style={fieldInput}>
            {CB_PAYMENT_MODES.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </Field>

        <Field label="Supplier name">
          <select value={header.supplier} onChange={(e) => setH("supplier", e.target.value)} style={fieldInput}>
            <option value="">— Select supplier —</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Outstanding">
          <input
            readOnly
            value={cbFmtINR(supplier?.outstanding || 0)}
            style={{ ...fieldMono, background: "var(--bg-sunken)", color: "var(--fg-muted)" }}
          />
        </Field>
        <Field label="PO number">
          <input
            value={header.poNumber}
            onChange={(e) => {
              /* Manual edit: keep the typed string, but drop any PO-linked numeric
                 id (it no longer matches what was typed). */
              setH("poNumber", e.target.value);
              setH("purchaseOrderId", null);
            }}
            placeholder="e.g. PO/2026-27/000001"
            style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
          />
        </Field>
        <Field label="Invoice number *">
          <input
            value={header.invoiceNumber}
            onChange={(e) => setH("invoiceNumber", e.target.value)}
            placeholder="e.g. MC-INV-44211"
            style={{
              ...fieldInput,
              fontFamily: "var(--font-mono)",
              border: header.invoiceNumber.trim() ? fieldInput.border : "1px solid var(--red-500, #d92d20)",
            }}
          />
        </Field>
        <Field label="Invoice amount *">
          <input
            type="number"
            step="0.001"
            value={header.invoiceAmount}
            onChange={(e) => setH("invoiceAmount", e.target.value)}
            placeholder="0.000"
            style={{
              ...fieldMono,
              border: num(header.invoiceAmount) > 0 ? fieldInput.border : "1px solid var(--red-500, #d92d20)",
            }}
          />
        </Field>
      </div>

      {/* Line 2 — dates / adjustments / remarks */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[repeat(7,minmax(110px,1fr))_minmax(180px,1.6fr)] lg:min-w-[1180px]"
        style={{
          gap: 8,
          marginTop: 10,
        }}
      >
        <Field label="Invoice date">
          <input
            type="date"
            value={header.invoiceDate}
            onChange={(e) => setH("invoiceDate", e.target.value)}
            style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
          />
        </Field>
        <Field label="Payment date">
          <input
            type="date"
            value={header.paymentDate}
            onChange={(e) => setH("paymentDate", e.target.value)}
            style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
          />
        </Field>
        <Field label="Bill discount %">
          <input
            type="number"
            step="0.01"
            value={header.discountPct}
            onChange={(e) => onBillPct(e.target.value)}
            placeholder="0.00"
            style={fieldMono}
          />
        </Field>
        <Field label="Bill discount amount">
          <input
            type="number"
            step="0.01"
            value={header.billDiscountAmount}
            onChange={(e) => onBillAmt(e.target.value)}
            placeholder="0.00"
            style={fieldMono}
          />
        </Field>

        <Field label="Other credits">
          <input
            type="number"
            step="0.001"
            value={header.otherCredits}
            onChange={(e) => setH("otherCredits", e.target.value)}
            placeholder="0.000"
            style={fieldMono}
          />
        </Field>
        <Field label="Other debits">
          <input
            type="number"
            step="0.001"
            value={header.otherDebits}
            onChange={(e) => setH("otherDebits", e.target.value)}
            placeholder="0.000"
            style={fieldMono}
          />
        </Field>
        <Field label="Coin adjustment">
          <input
            type="number"
            step="0.001"
            value={header.coinAdjustment}
            onChange={(e) => setH("coinAdjustment", e.target.value)}
            placeholder="0.000"
            style={fieldMono}
          />
        </Field>
        <Field label="Remarks">
          <input
            value={header.remarks}
            onChange={(e) => setH("remarks", e.target.value)}
            placeholder="Optional notes…"
            style={fieldInput}
          />
        </Field>
        <InvoiceCopyField
          grnId={grnId}
          url={header.invoiceDocumentUrl}
          fileName={header.invoiceDocumentFileName}
          onAttached={(url, name) => {
            setH("invoiceDocumentUrl", url);
            setH("invoiceDocumentFileName", name);
          }}
        />
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          color: "var(--green-700)",
          fontFamily: "var(--font-mono)",
        }}
      >
        Current supplier's GST number: {supplier?.gstin || "—"} · Terms {supplier?.terms || "—"}
      </div>
      </div>
      )}
    </div>
  );
};

/* Computed totals — sits below the products table on the one-page form so the
   reconciliation numbers stay next to the post button. */
const ComputedSummary = ({ totals }) => (
  <div
    style={{
      background: "var(--bg-panel)",
      border: "1px solid var(--border-default)",
      borderRadius: 6,
      padding: "12px 16px",
    }}
  >
    <Eyebrow style={{ display: "block", marginBottom: 10 }}>Computed (updates as products are added)</Eyebrow>
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7" style={{ gap: 10 }}>
      <SummaryCell label="Gross amount" value={cbFmtINR(totals.gross)} />
      <SummaryCell label="Goods value" value={cbFmtINR(totals.goodsValue)} />
      {/* Scheme + trade + bill discount — gross minus this is the goods value. */}
      <SummaryCell label="Total discount" value={cbFmtINR(totals.discount)} />
      <SummaryCell label="Total tax" value={cbFmtINR(totals.tax)} />
      <SummaryCell label="Computed amount" value={cbFmtINR(totals.computed)} />
      <SummaryCell
        label={
          Math.abs(totals.difference) >= 0.01 && Math.abs(totals.difference) < ROUND_OFF_TOLERANCE
            ? "Round off"
            : "Difference"
        }
        value={cbFmtINR(totals.difference)}
        tone={Math.abs(totals.difference) < ROUND_OFF_TOLERANCE ? "ok" : "warn"}
      />
      <SummaryCell label="Total items" value={cbFmtNum(totals.items)} />
    </div>
  </div>
);

const SummaryCell = ({ label, value, tone }) => (
  <div>
    <div
      style={{
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "var(--fg-muted)",
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 14,
        fontWeight: 700,
        marginTop: 2,
        color: tone === "warn" ? "var(--red-700)" : tone === "ok" ? "var(--green-700)" : "var(--fg-strong)",
      }}
    >
      {value}
    </div>
  </div>
);

/* Warning confirmation popup for destructive, non-undoable actions inside the
   drawer. z-index sits above the drawer (60) so it is not clipped by it. */
const ConfirmDialog = ({ title, message, confirmLabel = "Confirm", onConfirm, onCancel }) => (
  <div style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div onClick={onCancel} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
    <div
      role="alertdialog"
      aria-modal="true"
      style={{
        position: "relative",
        width: "min(420px, 92vw)",
        background: "var(--bg-panel)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        boxShadow: "0 12px 40px rgba(13,17,27,0.22)",
        padding: "18px 20px",
      }}
      className="cb-animate-in"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            flexShrink: 0,
            background: "var(--amber-50, var(--bg-sunken))",
            color: "var(--amber-700, var(--red-700))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          !
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--fg-strong)" }}>{title}</div>
      </div>
      <div style={{ fontSize: 12, color: "var(--fg-muted)", lineHeight: 1.5 }}>{message}</div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  </div>
);

/* Field wrapper for the request modal. Defined at module scope on purpose: a
   component created inside a render is a NEW component type every keystroke, so
   React unmounts and remounts the input, dropping focus after a single
   character. */
const ReqField = ({ label, children, span }) => (
  <div style={{ gridColumn: span ? `span ${span}` : undefined }}>
    <label style={fieldLabel}>{label}</label>
    {children}
  </div>
);

/* Raise a request for a medicine the master doesn't carry yet. Opened from the
   Add-product search when nothing matches, so the operator can record what the
   supplier actually shipped without abandoning the GRN. Prefilled from whatever
   is already on screen — the typed name, the invoice header, and any pricing the
   operator had started entering. */
const MedicineRequestModal = ({ initialName, header, supplier, entry, onClose, onSubmitted }) => {
  const [form, setForm] = useState(() => ({
    medicineName: initialName || "",
    manufacturer: "",
    composition: "",
    packing: entry?.packing || "",
    unitPack: entry?.unit || "",
    hsnCode: entry?.taxCategory || "",
    gstPercentage: entry?.taxPct ?? "",
    mrp: entry?.mrp ?? "",
    purchasePrice: entry?.purchasePrice ?? "",
    /* From the GRN header — the invoice that prompted the request, so whoever
       adds the medicine can check it against the supplier's own document. */
    invoiceNumber: header?.invoiceNumber || "",
    invoiceDate: header?.invoiceDate || "",
    supplierName: supplier?.name || "",
    remarks: "",
    /* Who the master team calls back. The number is mandatory server-side, so it
       is prefilled from the session rather than asked for — an operator mid-GRN
       should not have to look up their own mobile. Both stay editable. */
    requesterName: getCurrentUserName() === "system" ? "" : getCurrentUserName(),
    requesterNumber: getCurrentUserMobile(),
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /* The medicine name and a contact number are what the backend insists on;
     everything else is helpful context the master team can fill in, and blocking
     on it would defeat the point of not stalling the GRN. */
  const numberErr = phoneError(form.requesterNumber);
  /* HSN and GST stay optional — a requester mid-GRN may genuinely not know them,
     and the comment above is deliberate about not stalling on context the master
     team can fill in. But whatever IS entered has to be well-formed: this request
     becomes master data, and a malformed HSN here is what later prefills a GRN
     line and lands on a tax invoice. So: empty is fine, wrong is not. */
  const hsnErr = form.hsnCode !== "" && !isValidHsn(form.hsnCode);
  const gstErr = form.gstPercentage !== "" && !isValidGst(form.gstPercentage);
  const canSubmit =
    form.medicineName.trim().length > 0 &&
    form.requesterNumber.trim().length > 0 &&
    !numberErr &&
    !hsnErr &&
    !gstErr &&
    !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setErr(null);
    try {
      /* Same call the Master › Medicine Requests screen uses, so both entry
         points send an identical body and land in the same approval queue. */
      await supplyService.createMedicineRequest({
        ...form,
        medicineName: form.medicineName.trim(),
        gstPercentage: form.gstPercentage === "" ? null : num(form.gstPercentage),
        mrp: form.mrp === "" ? null : num(form.mrp),
        purchasePrice: form.purchasePrice === "" ? null : num(form.purchasePrice),
        unitPack: form.unitPack === "" ? null : num(form.unitPack),
        warehouseId: header?.store || undefined,
        supplierId: header?.supplier || undefined,
      });
      toast.success(`Requested “${form.medicineName.trim()}” to be added to the master.`);
      onSubmitted(form.medicineName.trim());
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Could not submit the request.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
      <div
        role="dialog"
        aria-modal="true"
        className="cb-animate-in"
        style={{
          position: "relative",
          width: "min(760px, 94vw)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          background: "var(--bg-panel)",
          border: "1px solid var(--border-default)",
          borderRadius: 8,
          boxShadow: "0 24px 64px rgba(13,17,27,0.22)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--border-default)" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--fg-strong)" }}>Request a new medicine</div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
            Not in the medicine master — send these details so it can be added.
          </div>
        </div>

        <div style={{ padding: 18, overflow: "auto", display: "flex", flexDirection: "column", gap: 12 }} className="cb-scroll">
          <div className="grid grid-cols-2 sm:grid-cols-3" style={{ gap: 12 }}>
            <ReqField label="Medicine name *" span={2}>
              <input
                value={form.medicineName}
                onChange={(e) => setF("medicineName", e.target.value)}
                placeholder="As printed on the supplier invoice"
                style={{
                  ...fieldInput,
                  borderColor: form.medicineName.trim() ? fieldInput.borderColor : "var(--red-500)",
                }}
              />
            </ReqField>
            <ReqField label="Manufacturer">
              <input value={form.manufacturer} onChange={(e) => setF("manufacturer", e.target.value)} style={fieldInput} />
            </ReqField>
            <ReqField label="Composition / strength" span={2}>
              <input value={form.composition} onChange={(e) => setF("composition", e.target.value)} style={fieldInput} />
            </ReqField>
            <ReqField label="Packing">
              <input
                value={form.packing}
                onChange={(e) => setF("packing", e.target.value)}
                placeholder="1X15"
                style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
              />
            </ReqField>
            <ReqField label="Unit pack">
              <input
                type="text"
                inputMode="numeric"
                value={form.unitPack}
                onChange={(e) => setF("unitPack", e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="10"
                style={fieldInput}
              />
            </ReqField>
            <ReqField label="HSN code">
              <input
                value={form.hsnCode}
                onChange={(e) => setF("hsnCode", e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="6 or 8 digits"
                title={`HSN code — ${HSN_RULE_TEXT}`}
                maxLength={8}
                style={{
                  ...fieldInput,
                  fontFamily: "var(--font-mono)",
                  borderColor: hsnErr ? "var(--red-500)" : undefined,
                }}
              />
              {hsnErr && (
                <div style={{ fontSize: 10, color: "var(--red-700)", marginTop: 3 }}>
                  HSN must be {HSN_RULE_TEXT}.
                </div>
              )}
            </ReqField>
            <ReqField label="GST %">
              <input
                type="number"
                value={form.gstPercentage}
                onChange={(e) => setF("gstPercentage", e.target.value)}
                title="One of 0, 5, 12, 18, 28"
                style={{ ...fieldMono, borderColor: gstErr ? "var(--red-500)" : undefined }}
              />
              {gstErr && (
                <div style={{ fontSize: 10, color: "var(--red-700)", marginTop: 3 }}>
                  GST must be one of 0, 5, 12, 18, 28.
                </div>
              )}
            </ReqField>
            <ReqField label="MRP">
              <input type="number" value={form.mrp} onChange={(e) => setF("mrp", e.target.value)} style={fieldMono} />
            </ReqField>
            <ReqField label="Purchase price">
              <input type="number" value={form.purchasePrice} onChange={(e) => setF("purchasePrice", e.target.value)} style={fieldMono} />
            </ReqField>
            <div />
          </div>

          <Eyebrow style={{ display: "block", marginTop: 2 }}>Raised against</Eyebrow>
          <div className="grid grid-cols-2 sm:grid-cols-3" style={{ gap: 12 }}>
            <ReqField label="Invoice number">
              <input
                value={form.invoiceNumber}
                onChange={(e) => setF("invoiceNumber", e.target.value)}
                placeholder="e.g. MC-INV-44211"
                style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
              />
            </ReqField>
            <ReqField label="Invoice date">
              <input
                type="date"
                value={form.invoiceDate}
                onChange={(e) => setF("invoiceDate", e.target.value)}
                style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
              />
            </ReqField>
            <ReqField label="Supplier">
              <input value={form.supplierName} onChange={(e) => setF("supplierName", e.target.value)} style={fieldInput} />
            </ReqField>
          </div>

          <ReqField label="Remarks">
            <textarea
              value={form.remarks}
              onChange={(e) => setF("remarks", e.target.value)}
              rows={2}
              placeholder="Anything else that helps identify the product…"
              style={{ ...fieldInput, height: "auto", padding: 8, resize: "vertical" }}
            />
          </ReqField>

          <Eyebrow style={{ display: "block", marginTop: 2 }}>Who to contact</Eyebrow>
          <div className="grid grid-cols-2 sm:grid-cols-3" style={{ gap: 12 }}>
            <ReqField label="Requested by">
              <input
                value={form.requesterName}
                onChange={(e) => setF("requesterName", e.target.value)}
                placeholder="Your name"
                style={fieldInput}
              />
            </ReqField>
            <ReqField label="Mobile *">
              {/* The decision comes back to this number on WhatsApp, and the
                  backend rejects a request without it. */}
              <input
                value={form.requesterNumber}
                onChange={(e) => setF("requesterNumber", e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                inputMode="numeric"
                maxLength={10}
                placeholder="9876543210"
                style={{
                  ...fieldInput,
                  fontFamily: "var(--font-mono)",
                  borderColor: form.requesterNumber.trim() && !numberErr ? fieldInput.borderColor : "var(--red-500)",
                }}
              />
              {numberErr && (
                <div style={{ fontSize: 10, color: "var(--red-700)", marginTop: 4 }}>{numberErr}</div>
              )}
            </ReqField>
            <div />
          </div>

          {err && <InlineAlert>{err}</InlineAlert>}
        </div>

        <div
          style={{
            padding: "12px 18px",
            borderTop: "1px solid var(--border-default)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" icon={<IconCheck size={14} />} onClick={submit} disabled={!canSubmit}>
            {saving ? "Submitting…" : "Submit request"}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Line items ---------------- */
const blankEntry = () => ({
  medId: "",
  productCode: "",
  productName: "",
  batch: "",
  quantity: "",
  free: "",
  expiry: "",
  discountPct: "",
  discountAmount: "",
  /* Supplier scheme discount — a flat ₹ amount off the line gross, deducted
     before the trade discount (the two cascade). Populated by the Curebay import
     from the file's "Scheme Discount" column; typed in by hand otherwise. */
  schemeDiscount: "",
  mrp: "",
  purchasePrice: "",
  taxCategory: "",
  purchaseTax: "",
  taxPct: "",
  packing: "",
  unit: "",
  /* Put-away location. `rackId` is what posts; `rackCode` is only a display
     fallback for a rack that is not in the store's loaded list (an imported code
     that resolved, or a since-deactivated rack on an edited GRN). */
  rackId: null,
  rackCode: "",
});

/* Column order for the bulk-upload template / parser. Mirrors the supplier
   invoice layout (see GRN_NOTES_API_SPEC §2): discount is a flat amount, GST is
   the line rate, HSN + packing captured. No "Sale Price" column — the pharmacy
   sells at MRP, so MRP is what gets posted as the selling price. A "Sale Price"
   column in an older file is simply ignored. Header aliases below stay accepted
   by the parser for older templates. */
const BULK_COLUMNS = [
  "Product Code",
  "Product Name",
  "HSN",
  "Batch",
  "Expiry (MM/YY)",
  "Quantity",
  "Free",
  "MRP",
  "Purchase Price",
  "Discount Amount",
  "GST %",
  "Packing",
  "Rack",
];

/* Read a value from a parsed sheet row, tolerating header aliases / casing. */
const pickCol = (row, ...keys) => {
  for (const k of keys) {
    const hit = Object.keys(row).find((rk) => String(rk).trim().toLowerCase() === k.toLowerCase());
    if (hit !== undefined && row[hit] !== "") return row[hit];
  }
  return "";
};

/* Typeahead for the Product name field — searches the ES medicine-name service
   after the user types 3+ characters (debounced 300 ms). */
const MED_SEARCH_PAGE_SIZE = 20;
const MedicineSearchField = ({ value, onSelect, onClear, onRequestNew }) => {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false); // first page
  const [loadingMore, setLoadingMore] = useState(false); // subsequent pages
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = React.useRef(null);
  const listRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const loadingMoreRef = React.useRef(false); // sync guard against double-fire
  /* Viewport rect of the input. The entry row is inside an overflow-x scroller,
     which clips absolutely-positioned children — so the dropdown is portalled to
     <body> and positioned fixed against this rect instead. */
  const [anchor, setAnchor] = useState(null);

  /* Sync the input when the parent resets the line (e.g. after Add / Cancel edit). */
  useEffect(() => {
    setQ(value || "");
  }, [value]);

  /* Debounced first-page search — fires 300 ms after the last keystroke if q has
     3+ chars. Resets pagination. */
  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setLoading(false);
      setHasMore(false);
      setPage(1);
      return undefined;
    }
    setLoading(true);
    let cancelled = false;
    const handle = setTimeout(() => {
      supplyService
        .searchMedicinesB2B(trimmed, { pageNo: 1, pageSize: MED_SEARCH_PAGE_SIZE })
        .then((items) => {
          if (cancelled) return;
          const list = items || [];
          setResults(list);
          setPage(1);
          setHasMore(list.length >= MED_SEARCH_PAGE_SIZE);
          setLoading(false);
          if (listRef.current) listRef.current.scrollTop = 0;
        })
        .catch(() => {
          if (cancelled) return;
          setResults([]);
          setHasMore(false);
          setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [q]);

  /* Fetch and append the next page when the list is scrolled near the bottom. */
  const loadMore = () => {
    const trimmed = q.trim();
    if (loadingMoreRef.current || !hasMore || loading || trimmed.length < 3) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = page + 1;
    supplyService
      .searchMedicinesB2B(trimmed, { pageNo: nextPage, pageSize: MED_SEARCH_PAGE_SIZE })
      .then((items) => {
        const list = items || [];
        setResults((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          return [...prev, ...list.filter((m) => !seen.has(m.id))];
        });
        setPage(nextPage);
        setHasMore(list.length >= MED_SEARCH_PAGE_SIZE);
      })
      .catch(() => {
        /* keep whatever we already have on a failed page fetch */
      })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  };

  const onListScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) loadMore();
  };

  /* Close on outside click. The menu is portalled out of boxRef, so it has to be
     checked separately or picking a result would close before the click lands. */
  useEffect(() => {
    const onDocClick = (e) => {
      const inBox = boxRef.current && boxRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inBox && !inMenu) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleChange = (e) => {
    setQ(e.target.value);
    setOpen(true);
    if (!e.target.value.trim() && onClear) onClear();
  };

  const pick = (m) => {
    onSelect(m);
    setQ(m.medicineName);
    setOpen(false);
  };

  const showDropdown = open && q.trim().length >= 3;
  const showHint = open && q.trim().length > 0 && q.trim().length < 3;

  /* "Request to add …" — the way out of a dead end, so it appears only when the
     search came back empty. While there are hits the operator's job is to pick
     one, and an always-visible request row next to real matches just invites a
     duplicate master entry. */
  const requestRow = () =>
    onRequestNew ? (
      <div
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          setOpen(false);
          onRequestNew(q.trim());
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-subtle)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--bg-panel)")}
        style={{
          padding: "8px 10px",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--ink-700)",
          cursor: "pointer",
          borderTop: "1px solid var(--border-default)",
          /* Plain panel fill, not a tint — a tinted row reads as selected. */
          background: "var(--bg-panel)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <IconPlus size={12} />
        Request to add “{q.trim()}”
      </div>
    ) : null;

  /* Keep the portalled menu glued to the input while anything scrolls or resizes.
     `true` on the scroll listener catches the drawer body and the entry-row
     scroller, which don't bubble scroll events. */
  useLayoutEffect(() => {
    if (!showDropdown && !showHint) return undefined;
    const measure = () => {
      const el = boxRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setAnchor({ top: r.bottom + 2, left: r.left, width: r.width });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [showDropdown, showHint]);

  /* Portal to <body> so no overflow / transform ancestor can clip the menu. */
  const inPortal = (node) =>
    anchor
      ? createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: anchor.top, left: anchor.left, width: anchor.width, zIndex: 90 }}
          >
            {node}
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <input
        value={q}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        placeholder="Type 3+ chars to search…"
        style={fieldInput}
      />
      {showHint &&
        inPortal(
        <div
          style={{
            padding: "6px 10px",
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
            fontSize: 11,
            color: "var(--fg-muted)",
          }}
        >
          Type at least 3 characters to search.
        </div>
      )}
      {showDropdown &&
        inPortal(
        <div
          ref={listRef}
          onScroll={onListScroll}
          style={{
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
            boxShadow: "0 8px 20px rgba(13,17,27,0.12)",
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          {loading && (
            <div style={{ padding: "8px 10px", fontSize: 11, color: "var(--fg-muted)" }}>Searching…</div>
          )}
          {/* Nothing came back — the only useful next step is to ask for it. */}
          {!loading && results.length === 0 && (
            <>
              <div style={{ padding: "8px 10px", fontSize: 11, color: "var(--fg-muted)" }}>
                No medicines match “{q.trim()}”.
              </div>
              {requestRow()}
            </>
          )}
          {results.map((m) => (
            <div
              key={m.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(m)}
              style={{
                padding: "6px 10px",
                cursor: "pointer",
                borderBottom: "1px solid var(--border-subtle)",
                fontSize: 12,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-subtle)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{m.medicineName}</div>
              {/* How the product is sold ("strip of 15 capsule pr") — the quickest
                  way to tell two same-named packs apart before picking one. */}
              {m.medicineTypeOfSell && (
                <div style={{ fontSize: 10, color: "var(--fg-default)", marginTop: 1 }}>
                  {m.medicineTypeOfSell}
                </div>
              )}
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--fg-muted)",
                  marginTop: 2,
                }}
              >
                {m.id}
                {m.medicineRate ? ` · ₹${m.medicineRate}` : ""}
                {m.unitPack ? ` · pack ${m.unitPack}` : ""}
              </div>
            </div>
          ))}
          {!loading && loadingMore && (
            <div style={{ padding: "8px 10px", fontSize: 11, color: "var(--fg-muted)", textAlign: "center" }}>
              Loading more…
            </div>
          )}
          {!loading && !hasMore && results.length > 0 && (
            <div style={{ padding: "6px 10px", fontSize: 10, color: "var(--fg-subtle)", textAlign: "center" }}>
              End of results
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* Bulk template download + file parsing. Lives in a hook because the drawer's
   footer owns the buttons while the line list owns the parsed rows.

   Two upload formats:
     custom  — the in-house template (BULK_COLUMNS), parsed below.
     curebay — a Curebay-issued supplier invoice export, parsed by
               grnCurebayImport.js. That format also carries the invoice number
               and date, which are pushed into the GRN header via `setH`. */
export const GRN_IMPORT_FORMATS = [
  { id: "custom", label: "Custom format" },
  { id: "curebay", label: "Curebay format" },
];

const useBulkImport = ({ setLines, setH, medicines, format }) => {
  const [importMsg, setImportMsg] = useState(null);
  const fileRef = React.useRef(null);

  /* Download a pre-formatted .xlsx template with one sample row. */
  const downloadCurebayTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([CUREBAY_COLUMNS, CUREBAY_SAMPLE_ROW]);
    /* Dates must stay text — stored as real dates, Excel hands the parser a
       serial number instead of "16/07/26" / "01/2028". */
    if (ws.B2) ws.B2 = { t: "s", v: "16/07/26" };
    if (ws.G2) ws.G2 = { t: "s", v: "01/2028" };
    ws["!cols"] = CUREBAY_COLUMNS.map(() => ({ wch: 18 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Curebay invoice");
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([out], { type: "application/octet-stream" }), "grn-curebay-template.xlsx");
  };

  const downloadCustomTemplate = () => {
    const sample = [
      "OLYMPRIX-20", // Product Code
      "Olymprix-20 Tablet", // Product Name
      "", // HSN — left blank on purpose: a sample code gets copied through
      "24443791", // Batch
      "09/26", // Expiry (MM/YY)
      2, // Quantity
      0, // Free
      186.15, // MRP — also posted as the selling price
      132.96, // Purchase Price
      10.64, // Discount Amount (flat ₹)
      5, // GST %
      "1X15", // Packing — printed pack label; unit pack is derived from it
      "RACK-A", // Rack
    ];
    const ws = XLSX.utils.aoa_to_sheet([BULK_COLUMNS, sample]);
    /* Expiry must stay text — if Excel stores it as a date the parser receives a
       serial number, not "09/26". Force the cell to a string cell. */
    if (ws.E2) ws.E2 = { t: "s", v: "09/26" };
    ws["!cols"] = BULK_COLUMNS.map(() => ({ wch: 16 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GRN lines");
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([out], { type: "application/octet-stream" }), "grn-bulk-template.xlsx");
  };

  const downloadTemplate = () =>
    format === "curebay" ? downloadCurebayTemplate() : downloadCustomTemplate();

  /* Curebay format — parsing lives in grnCurebayImport.js; this only applies the
     result. The file carries the supplier invoice number and date, so they seed
     the GRN header (without clobbering anything already typed there). */
  const applyCurebayRows = (rows) => {
    const { lines: parsed, skipped, skippedReasons, unmapped, invoiceNumber, invoiceDate } =
      parseCurebayRows(rows);
    if (parsed.length) setLines((prev) => [...prev, ...parsed]);

    const filled = [];
    if (invoiceNumber && setH) {
      setH("invoiceNumber", invoiceNumber);
      filled.push(`invoice ${invoiceNumber}`);
    }
    if (invoiceDate && setH) {
      setH("invoiceDate", invoiceDate);
      filled.push(`dated ${invoiceDate}`);
    }

    /* Name what was dropped and why — a bare count reads as data loss. */
    const why = Object.entries(skippedReasons || {})
      .map(([reason, n]) => `${n} ${reason}`)
      .join(", ");

    setImportMsg({
      tone: parsed.length ? (skipped || unmapped ? "warn" : "ok") : "warn",
      text: parsed.length
        ? `Curebay format · ${parsed.length} line${parsed.length !== 1 ? "s" : ""} imported${
            skipped ? ` · ${skipped} incomplete row${skipped !== 1 ? "s" : ""} skipped (${why})` : ""
          }${
            unmapped
              ? ` · ⚠ ${unmapped} line${unmapped !== 1 ? "s" : ""} have no CBDrugCode — pick the product on each before posting`
              : ""
          }${filled.length ? ` · header filled from file (${filled.join(", ")})` : ""}`
        : `No usable rows found${why ? ` — ${why}` : ""}. Check that this is a Curebay invoice export.`,
    });
  };

  /* Parse an uploaded CSV / XLSX into GRN line items. */
  const handleBulkFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (format === "curebay") {
          applyCurebayRows(rows);
          return;
        }
        const parsed = [];
        let skipped = 0;
        rows.forEach((r) => {
          const code = String(pickCol(r, "Product Code", "SKU", "Sku Code")).trim();
          const name = String(pickCol(r, "Product Name", "Item Details")).trim();
          const med = medicines.find(
            (m) =>
              (code && m.sku.toLowerCase() === code.toLowerCase()) ||
              (name && m.name.toLowerCase() === name.toLowerCase())
          );
          const quantity = Number(pickCol(r, "Quantity", "Qty")) || 0;
          const batch = String(pickCol(r, "Batch", "Vendor Batch Number")).trim();
          if (!quantity || !batch) {
            skipped++;
            return;
          }
          parsed.push({
            medId: med?.id || "",
            productCode: med?.sku || code,
            productName: med?.name || name || "(unmapped product)",
            batch,
            quantity,
            free: Number(pickCol(r, "Free", "Free Qty")) || "",
            expiry: expiryFromCell(pickCol(r, "Expiry (MM/YY)", "Expiry (MM/YYYY)", "Expiry", "Expiry Date")),
            /* Discount amount is authoritative; "Discount %" still accepted for older templates. */
            discountAmount: pickCol(r, "Discount Amount", "Disc Amount"),
            discountPct: pickCol(r, "Discount %", "Discount Percentage"),
            mrp: pickCol(r, "MRP") || med?.mrp || "",
            purchasePrice: pickCol(r, "Purchase Price", "Unit Price", "Rate"),
            taxCategory: pickCol(r, "HSN", "HSN Code", "Tax Category") || med?.hsn || "",
            purchaseTax: med ? `GST ${med.gst}%` : "",
            taxPct: pickCol(r, "GST %", "GST", "Tax %", "Tax") || med?.gst || "",
            /* Printed pack label kept verbatim ("1X15", "200ML"); unit pack is the
               integer derived from it. Normalise `15'S` / `1*15` style cells to the
               integer the backend wants; a pack-less row imports blank and is
               flagged in the table until the operator fills it. */
            packing: String(pickCol(r, "Packing", "Pack", "Unit") || med?.pack || ""),
            unit: toUnitPack(pickCol(r, "Packing", "Pack", "Unit") || med?.pack || ""),
            /* The file gives a rack CODE; the id it maps to is resolved against
               the store's rack list once the lines land (see resolveRacks in
               LineItemsSection). An unknown code resolves to nothing and the
               operator picks the rack. */
            rackId: null,
            rackCode: String(pickCol(r, "Rack") || "").trim(),
          });
        });
        if (parsed.length) setLines((prev) => [...prev, ...parsed]);
        setImportMsg({
          tone: parsed.length ? (skipped ? "warn" : "ok") : "warn",
          text: parsed.length
            ? `Custom format · ${parsed.length} line${parsed.length !== 1 ? "s" : ""} imported${
                skipped ? ` · ${skipped} row${skipped !== 1 ? "s" : ""} skipped (missing batch / quantity)` : ""
              }`
            : "No valid rows found — check the template columns, or switch the format to Curebay.",
        });
      } catch (err) {
        setImportMsg({ tone: "warn", text: "Could not read that file — use the CSV / XLS / XLSX template format." });
      }
    };
    reader.onerror = () => {
      setImportMsg({ tone: "warn", text: "The file could not be read. Try again or pick another file." });
    };
    try {
      reader.readAsArrayBuffer(file);
    } catch (err) {
      setImportMsg({ tone: "warn", text: "The file could not be opened." });
    }
  };

  /* Cleared explicitly (e.g. "Reset all"), never reactively on lines.length — a
     failed import reports "No valid rows found" while lines is still empty, and
     that message has to stay on screen. */
  const clearImportMsg = () => setImportMsg(null);

  return { fileRef, importMsg, clearImportMsg, downloadTemplate, handleBulkFile };
};

const LineItemsSection = ({
  lines,
  setLines,
  header,
  supplier,
  onClearImportMsg,
  totals,
  pricing,
  priceErr,
  warnings,
  pricedByRef,
}) => {
  const [entry, setEntry] = useState(blankEntry());
  const [editIdx, setEditIdx] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  /* Name typed into the product search when the operator asked to have it added
     to the master; null when the request modal is closed. */
  const [requestName, setRequestName] = useState(null);
  const [confirmClearLines, setConfirmClearLines] = useState(false);

  /* ---------------- Put-away racks ----------------
     The receiving store's racks, loaded once per store, and the medicine → home
     shelf map that prefills each line. Both are per-warehouse: changing the store
     reloads the list and drops what was resolved against the old one. */
  const warehouseId = header.store;
  const [racks, setRacks] = useState([]);
  const [racksErr, setRacksErr] = useState(null);
  /* Medicine codes already looked up — found or not. Without this the prefill
     effect below would re-query the same codes on every lines change. */
  const homesTried = React.useRef(new Set());
  /* The store the racks currently on the lines were resolved against. */
  const rackedFor = React.useRef(warehouseId);

  useEffect(() => {
    homesTried.current = new Set();
    /* A rack belongs to exactly one store, so switching the receiving store
       invalidates every rack already set — the backend rejects a rack from
       another warehouse outright. Drop them and let the prefill below re-resolve
       against the new store. Guarded on an actual change: running this on mount
       would wipe the racks an edited GRN had just loaded. */
    if (rackedFor.current !== warehouseId) {
      rackedFor.current = warehouseId;
      setLines((prev) =>
        prev.some((l) => l.rackId != null)
          ? prev.map((l) => (l.rackId != null ? { ...l, rackId: null, rackCode: "" } : l))
          : prev
      );
      setEntry((e) => (e.rackId != null ? { ...e, rackId: null, rackCode: "" } : e));
    }
    if (!warehouseId) {
      setRacks([]);
      setRacksErr(null);
      return undefined;
    }
    let alive = true;
    setRacksErr(null);
    rackService
      .listAll({ warehouseId })
      .then((list) => {
        if (alive) setRacks(list || []);
      })
      .catch((e) => {
        if (!alive) return;
        setRacks([]);
        setRacksErr(e?.response?.data?.message || e?.message || "Could not load this store's racks.");
      });
    return () => {
      alive = false;
    };
  }, [warehouseId]);

  useEffect(() => {
    if (!warehouseId) return undefined;
    let alive = true;

    /* A bulk-imported "Rack" column gives a code, not an id. Resolve it against
       the store's racks — case-insensitively, as the backend's unique
       (warehouse, code) index is. An unknown code stays unresolved and the
       operator picks the rack. */
    if (racks.length) {
      setLines((prev) => {
        let touched = false;
        const next = prev.map((l) => {
          if (l.rackId != null || !l.rackCode) return l;
          const hit = racks.find((r) => r.code.toLowerCase() === String(l.rackCode).trim().toLowerCase());
          if (!hit) return l;
          touched = true;
          return { ...l, rackId: hit.id, rackCode: hit.code };
        });
        return touched ? next : prev;
      });
    }

    /* Prefill from each medicine's home shelf — one call covers every code not
       looked up yet, however the lines arrived (typed, imported, or from a PO).
       Only lines with no rack are filled: a rack the operator cleared or chose
       stays as they left it. */
    const fresh = [
      ...new Set(
        lines
          .filter((l) => l.rackId == null && l.medId)
          .map((l) => String(l.medId))
          .filter((c) => !homesTried.current.has(c))
      ),
    ];
    if (!fresh.length) return undefined;
    fresh.forEach((c) => homesTried.current.add(c));
    rackService
      .lookup({ warehouseId, medicineCodes: fresh })
      .then((homes) => {
        if (!alive || !homes.size) return;
        setLines((prev) => {
          let touched = false;
          const next = prev.map((l) => {
            if (l.rackId != null || !l.medId) return l;
            const home = homes.get(String(l.medId));
            if (!home?.rackId) return l;
            touched = true;
            return { ...l, rackId: home.rackId, rackCode: home.rackCode };
          });
          return touched ? next : prev;
        });
      })
      /* A failed prefill is not worth interrupting a receipt for — the picker
         still works, it just starts empty. */
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [lines, racks, warehouseId, setLines]);

  /* Create the rack the operator typed. Goods inward happens at the shelf, and a
     rack number that exists on the floor but not in the master must not hold up a
     receipt — so the picker collects the details and the new rack is selected.
     `details` is { code, name, description } from the create dialog; a blank name
     defaults to the code server-side. */
  const createRack = (details) =>
    rackService.create({ warehouseId, ...details }).then((rack) => {
      setRacks((prev) => [...prev, rack].sort((a, b) => a.code.localeCompare(b.code)));
      toast.success(`Rack ${rack.code} created in ${warehouseId}.`);
      return rack;
    });

  /* Anything typed into the entry row (vs. a pristine blank form) — gates the
     Reset button so it only offers itself when there is something to discard. */
  const entryDirty = Object.values(entry).some((v) => v !== "" && v != null);

  const resetEntry = () => {
    setEntry(blankEntry());
    setEditIdx(null);
    setConfirmReset(false);
  };

  /* Drop every line. Also leaves edit mode — the line being edited is gone, so
     keeping editIdx would make Update write to an index that no longer exists. */
  const clearLines = () => {
    setLines([]);
    setEntry(blankEntry());
    setEditIdx(null);
    setConfirmClearLines(false);
    /* The "N lines imported" banner describes lines that no longer exist. */
    if (onClearImportMsg) onClearImportMsg();
  };

  const setE = (key, val) => setEntry((e) => ({ ...e, [key]: val }));

  /* Selecting a product from the B2B medicine search auto-fills name, code and
     pricing. The B2B ES record carries salePrice, purchasePrice/ptr, unitPack and
     unitPriceB2b; HSN / GST% aren't returned, so the operator still fills those.
     All prices come off the supplier invoice ultimately, so they stay editable. */
  const onSelectMedicine = (med) => {
    if (!med) {
      setEntry((e) => ({ ...e, medId: "", productCode: "", productName: "" }));
      return;
    }
    /* MRP: prefer explicit medicineRate/salePrice. Purchase: prefer the B2B unit
       price, falling back to purchasePrice / ptr. Coerce to numbers; blank if
       absent so the field shows empty rather than "0". */
    const numOr = (...vals) => {
      for (const v of vals) {
        if (v != null && v !== "") {
          const n = Number(v);
          if (!Number.isNaN(n)) return n;
        }
      }
      return "";
    };
    /* Re-mapping the product on an existing line only corrects which medicine the
       line points at. Everything else on that line came off the supplier invoice
       and the operator already captured it, so master data must not overwrite it —
       swap the name and code and leave the rest alone. Adding a new line still
       auto-fills, since there is nothing there to lose. */
    if (editIdx !== null) {
      setEntry((e) => ({ ...e, medId: med.id, productCode: med.id, productName: med.medicineName }));
      return;
    }

    const mrp = numOr(med.medicineRate, med.salePrice, med.unitPrice);
    const purchase = numOr(med.purchasePrice);
    const gst = med.gst ?? med.gstPercentage ?? med.taxPct;
    const hsn = med.hsnCode ?? med.hsn;
    setEntry((e) => ({
      ...e,
      medId: med.id,
      productCode: med.id,
      productName: med.medicineName,
      mrp,
      purchasePrice: purchase,
      packing: med.unitPack != null && med.unitPack !== "" ? String(med.unitPack) : e.packing,
      unit: toUnitPack(med.unitPack) || e.unit,
      /* Prefill GST% / HSN if the search row happens to carry them; otherwise the
         full B2B record is fetched on Add (commitEntry) and patched into the list. */
      taxPct: gst != null && gst !== "" ? gst : e.taxPct,
      taxCategory: hsn || e.taxCategory,
    }));

    /* The search-list rows omit HSN + GST%, so fetch the full B2B record for the
       selected medicine and fill those into the form (also carried into the list
       on Add). Guard: only apply if the operator hasn't picked another product. */
    supplyService.getMedicineDetailB2B(med.id).then((detail) => {
      if (!detail) return;
      const dGst = detail.gstPercentages ?? detail.gstPercentage ?? detail.gst;
      const dHsn = detail.hsnCode ?? detail.hsn;
      setEntry((e) => {
        if (e.medId !== med.id) return e;
        const gstLabel = dGst != null && dGst !== "" ? `GST ${dGst}%` : "";
        return {
          ...e,
          taxCategory: dHsn != null && dHsn !== "" ? String(dHsn) : e.taxCategory,
          taxPct: dGst != null && dGst !== "" ? dGst : e.taxPct,
          purchaseTax: gstLabel || e.purchaseTax,
          packing: e.packing || (detail.unitPack != null ? String(detail.unitPack) : ""),
          unit: e.unit || toUnitPack(detail.unitPack),
        };
      });
    });

    /* Where this medicine lives in this store, so the Rack field is already right
       for the common case. Same guard: only applied while it is still the selected
       product, and never over a rack the operator picked. */
    if (warehouseId) {
      rackService
        .lookup({ warehouseId, medicineCodes: [med.id] })
        .then((homes) => {
          const home = homes.get(String(med.id));
          if (!home?.rackId) return;
          setEntry((e) =>
            e.medId === med.id && e.rackId == null
              ? { ...e, rackId: home.rackId, rackCode: home.rackCode }
              : e
          );
        })
        .catch(() => {});
    }
  };

  /* Picking an existing batch copies its price + expiry. */
  const onPickBatch = (lot) => {
    setEntry((e) => ({
      ...e,
      batch: lot.batch,
      purchasePrice: lot.purchase,
      mrp: lot.mrp,
      expiry: toMMYY(lot.exp),
    }));
  };

  /* Discount % and amount are both entered manually — neither derives the other. */

  const entryValid =
    entry.medId &&
    num(entry.quantity) > 0 &&
    entry.batch.trim() &&
    isValidExpiry(entry.expiry) &&
    isValidGst(entry.taxPct) &&
    isValidHsn(entry.taxCategory) &&
    isValidUnitPack(entry.unit);

  const commitEntry = () => {
    if (!entryValid) return;
    /* Keep the committed object's identity so the async detail patch below can
       find this exact line even if the list is reordered/edited meanwhile. */
    const committed = entry;
    const wasEditing = editIdx !== null;
    if (wasEditing) {
      setLines((prev) => prev.map((l, i) => (i === editIdx ? committed : l)));
      setEditIdx(null);
    } else {
      setLines((prev) => [...prev, committed]);
    }
    setEntry(blankEntry());

    /* On Add, fetch the medicine's full B2B record and patch HSN / GST% (+ tax
       label and unit pack) onto the line just added — the search-list rows don't
       carry these. Matched by object identity; skips if the line was removed.
       Skipped on Update: the patch overwrites HSN / GST% from master data, which
       would silently undo a value the operator just corrected by hand. */
    if (!wasEditing && committed.medId) {
      supplyService.getMedicineDetailB2B(committed.medId).then((detail) => {
        if (!detail) return;
        const dGst = detail.gstPercentages ?? detail.gstPercentage ?? detail.gst;
        const dHsn = detail.hsnCode ?? detail.hsn;
        const gstLabel = dGst != null && dGst !== "" ? `GST ${dGst}%` : "";
        setLines((prev) =>
          prev.map((l) =>
            l === committed
              ? {
                  ...l,
                  taxCategory: dHsn != null && dHsn !== "" ? String(dHsn) : l.taxCategory,
                  taxPct: dGst != null && dGst !== "" ? dGst : l.taxPct,
                  purchaseTax: gstLabel || l.purchaseTax,
                  packing: l.packing || (detail.unitPack != null ? String(detail.unitPack) : ""),
                  unit: l.unit || toUnitPack(detail.unitPack),
                }
              : l
          )
        );
      });
    }
  };

  const editLine = (i) => {
    setEntry(lines[i]);
    setEditIdx(i);
  };
  /* Patch fields of an already-added line in place. Used by the Rack cell, which
     is a storage location rather than invoice data — the operator often assigns it
     while putting the stock away, and pulling the whole line back into the entry
     row just to set a rack is needless. */
  const patchLine = (i, patch) => {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };
  /* Confirmed in a dialog (see confirmDel) — a mis-click here silently drops a
     received line and its batch/expiry, which is not recoverable from the table. */
  const delLine = (i) => {
    setLines((prev) => prev.filter((_, idx) => idx !== i));
    if (editIdx === i) {
      setEditIdx(null);
      setEntry(blankEntry());
    } else if (editIdx !== null && editIdx > i) {
      /* Indices shift left after the removal — without this the entry row would
         be committing over the wrong line. */
      setEditIdx(editIdx - 1);
    }
  };

  /* Index of the line pending delete confirmation (null = no dialog open). */
  const [confirmDel, setConfirmDel] = useState(null);

  const [batchOptions, setBatchOptions] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);

  /* Refetch the existing batches for whichever medicine is currently in the entry row. */
  useEffect(() => {
    if (!entry.medId) {
      setBatchOptions([]);
      return undefined;
    }
    let alive = true;
    setBatchLoading(true);
    supplyService
      .getMedicineBatches(entry.medId)
      .then((items) => {
        if (!alive) return;
        setBatchOptions(items || []);
        setBatchLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setBatchOptions([]);
        setBatchLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [entry.medId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Product entry form */}
      <div style={{ background: "var(--bg-panel)", border: "1px solid var(--border-default)", borderRadius: 6, padding: "12px 16px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <Eyebrow>{editIdx !== null ? `Editing line ${editIdx + 1}` : "Add product"}</Eyebrow>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {editIdx !== null && (
              <button
                onClick={resetEntry}
                style={{ border: 0, background: "transparent", color: "var(--fg-link)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
              >
                Cancel edit
              </button>
            )}
            <button
              onClick={() => setConfirmReset(true)}
              disabled={!entryDirty}
              style={{
                border: 0,
                background: "transparent",
                color: entryDirty ? "var(--red-700)" : "var(--fg-subtle, var(--fg-muted))",
                fontSize: 11,
                fontWeight: 600,
                cursor: entryDirty ? "pointer" : "not-allowed",
              }}
            >
              Reset
            </button>
          </div>
        </div>

        {requestName !== null && (
          <MedicineRequestModal
            initialName={requestName}
            header={header}
            supplier={supplier}
            entry={entry}
            onClose={() => setRequestName(null)}
            onSubmitted={(name) => {
              setRequestName(null);
              /* Keep the typed name on the row so the operator can see what they
                 asked for; the line still needs a real product code before it
                 can be posted. */
              setEntry((e) => ({ ...e, productName: name }));
            }}
          />
        )}

        {confirmReset && (
          <ConfirmDialog
            title="Reset this entry?"
            message={
              editIdx !== null
                ? `The edits to line ${editIdx + 1} will be discarded and the form cleared. The line itself stays in the table.`
                : "Everything typed into the product form will be cleared. Lines already added to the table are not affected."
            }
            confirmLabel="Reset form"
            onConfirm={resetEntry}
            onCancel={() => setConfirmReset(false)}
          />
        )}

        {/* Single entry line — every field plus Add on one row. The wrapper scrolls
            horizontally on narrow screens rather than squashing the inputs. */}
        <div style={{ overflowX: "auto" }} className="cb-scroll">
        {/* One entry per field, in order, then `auto` for the Add button —
            Product name, Qty, Free, Batch, Expiry, Purch price, MRP, HSN,
            GST %, Packing, Unit pack, Scheme, Disc %, Disc amt, Rack. A field
            added without a column here lands in an implicit auto-sized one and
            shoves every later field a column to the left. On a phone the fields
            stack two per row instead. */}
        <div
          className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-[minmax(190px,1.7fr)_0.55fr_0.5fr_0.9fr_0.7fr_0.8fr_0.7fr_0.9fr_0.55fr_0.7fr_0.6fr_0.7fr_0.6fr_0.7fr_0.7fr_auto] lg:min-w-[1480px]"
          style={{
            gap: 8,
            alignItems: "start",
            paddingBottom: 4,
          }}
        >
          <Field label="Product name" className="col-span-2 lg:col-span-1">
            <MedicineSearchField
              value={entry.productName}
              onSelect={onSelectMedicine}
              onClear={() => onSelectMedicine(null)}
              onRequestNew={(name) => setRequestName(name)}
            />
          </Field>
          <Field label="Qty">
            <input type="number" value={entry.quantity} onChange={(e) => setE("quantity", e.target.value)} style={fieldMono} />
          </Field>
          <Field label="Free">
            <input type="number" value={entry.free} onChange={(e) => setE("free", e.target.value)} style={fieldMono} />
          </Field>
          <Field label="Batch">
            <input
              value={entry.batch}
              onChange={(e) => setE("batch", e.target.value)}
              placeholder="Batch no."
              style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
            />
          </Field>
          <Field label="Expiry *">
            <input
              value={entry.expiry}
              onChange={(e) => setE("expiry", formatExpiryInput(e.target.value, entry.expiry))}
              inputMode="numeric"
              maxLength={5}
              placeholder="MM/YY"
              title="Required · type digits only — the / is added for you (e.g. 0926 → 09/26)"
              style={{
                ...fieldInput,
                fontFamily: "var(--font-mono)",
                borderColor:
                  entry.expiry && !isValidExpiry(entry.expiry) ? "var(--red-500)" : fieldInput.borderColor,
              }}
            />
            {entry.expiry && !isValidExpiry(entry.expiry) && (
              <span style={{ fontSize: 10, color: "var(--red-700)" }}>MM/YY, not past</span>
            )}
          </Field>
          <Field label="Purch price">
            <input type="number" value={entry.purchasePrice} onChange={(e) => setE("purchasePrice", e.target.value)} style={fieldMono} />
          </Field>
          <Field label="MRP">
            <input
              type="number"
              value={entry.mrp}
              onChange={(e) => setE("mrp", e.target.value)}
              title="Also posted as the selling price — the pharmacy sells at MRP"
              style={fieldMono}
            />
          </Field>
          <Field label="HSN code">
            <input
              value={entry.taxCategory}
              onChange={(e) => setE("taxCategory", e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="6 or 8 digits"
              title={`Required · ${HSN_RULE_TEXT}`}
              maxLength={8}
              style={{
                ...fieldInput,
                fontFamily: "var(--font-mono)",
                borderColor:
                  entry.taxCategory !== "" && !isValidHsn(entry.taxCategory)
                    ? "var(--red-500)"
                    : undefined,
              }}
            />
            {entry.taxCategory !== "" && !isValidHsn(entry.taxCategory) && (
              <div style={{ fontSize: 10, color: "var(--red-700)", marginTop: 3 }}>
                HSN must be {HSN_RULE_TEXT}.
              </div>
            )}
          </Field>
          <Field label="GST %">
            <input
              type="number"
              value={entry.taxPct}
              onChange={(e) => setE("taxPct", e.target.value)}
              placeholder="5"
              title="Required · one of 0, 5, 12, 18, 28"
              style={{
                ...fieldMono,
                borderColor:
                  entry.taxPct !== "" && !isValidGst(entry.taxPct)
                    ? "var(--red-500)"
                    : fieldMono.borderColor,
              }}
            />
            {entry.taxPct !== "" && !isValidGst(entry.taxPct) && (
              <span style={{ fontSize: 10, color: "var(--red-700)" }}>0/5/12/18/28</span>
            )}
          </Field>
          <Field label="Packing">
            <input
              value={entry.packing}
              onChange={(e) => {
                /* Printed pack label. Typing it also seeds the numeric unit pack
                   (last digit run) until the operator overrides that field. */
                const v = e.target.value;
                setEntry((en) => ({ ...en, packing: v, unit: toUnitPack(v) || en.unit }));
              }}
              placeholder="1X15"
              title="Printed pack label from the invoice, e.g. 1X15, 10S, 200ML"
              style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
            />
          </Field>
          <Field label="Unit pack *">
            <input
              type="text"
              inputMode="numeric"
              value={entry.unit}
              onChange={(e) => setE("unit", e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="10"
              title="Required · units per strip, whole number 1 or more"
              style={{
                ...fieldInput,
                borderColor:
                  entry.unit !== "" && !isValidUnitPack(entry.unit) ? "var(--red-500)" : fieldInput.borderColor,
              }}
            />
            {entry.unit !== "" && !isValidUnitPack(entry.unit) && (
              <span style={{ fontSize: 10, color: "var(--red-700)" }}>Whole number ≥ 1</span>
            )}
          </Field>
          {/* "Scheme ₹", not "Scheme amt": the uppercased label has to fit this
              column on one line, or it wraps and drops the input below the row. */}
          <Field label="Scheme ₹">
            {/* Flat ₹ off the gross, taken before Disc % — the supplier's scheme
                line, not a trade discount. */}
            <input
              type="number"
              value={entry.schemeDiscount}
              onChange={(e) => setE("schemeDiscount", e.target.value)}
              title="Supplier scheme discount (₹) — deducted before the trade discount"
              style={fieldMono}
            />
          </Field>
          <Field label="Disc %">
            <input type="number" value={entry.discountPct} onChange={(e) => setE("discountPct", e.target.value)} style={fieldMono} />
          </Field>
          <Field label="Disc amt">
            <input
              type="number"
              value={entry.discountAmount}
              onChange={(e) => setE("discountAmount", e.target.value)}
              style={fieldMono}
            />
          </Field>
          <Field label="Rack">
            {/* Prefilled from the medicine's home shelf when there is one; the
                operator can override it, or create the rack they are standing at. */}
            <RackPicker
              value={entry.rackId}
              racks={racks}
              fallbackLabel={entry.rackCode}
              width="100%"
              height={30}
              disabled={!warehouseId}
              disabledHint="Select the receiving store first"
              warehouseLabel={warehouseId}
              placeholder={warehouseId ? "— none —" : "store?"}
              onChange={(rackId, rack) =>
                setEntry((e) => ({ ...e, rackId, rackCode: rack?.code || "" }))
              }
              onCreate={createRack}
            />
          </Field>
          {/* Pinned to the right edge of the scroller: the row is wider than the
              panel, so at any scroll position other than the far right the Add /
              Update button would sit off-screen. Opaque background + a left divider
              so the fields scroll under it cleanly. */}
          <div
            className="col-span-2 sm:col-span-4 lg:col-span-1 lg:sticky lg:right-0 lg:pl-[10px] lg:border-l lg:border-solid lg:border-[color:var(--border-subtle)]"
            style={{
              paddingTop: 18,
              background: "var(--bg-panel)",
            }}
          >
            <Button variant="primary" icon={<IconPlus size={14} />} onClick={commitEntry} disabled={!entryValid}>
              {editIdx !== null ? "Update" : "Add"}
            </Button>
          </div>
        </div>
        </div>

        {/* Existing batches for the selected product */}
        {entry.medId && (
          <div style={{ marginTop: 14 }}>
            <Eyebrow style={{ display: "block", marginBottom: 6 }}>
              Existing batches · {entry.productName} — click to copy price &amp; expiry
            </Eyebrow>
            <div style={{ border: "1px solid var(--border-default)", borderRadius: 4, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "var(--bg-subtle)" }}>
                    {["Batch", "Purchase price", "Sale price", "Stock", "MRP", "Expiry", "Last received", "Location"].map(
                      (h, i) => (
                        <th key={h} style={{ ...grnTh, textAlign: i > 0 && i < 5 ? "right" : "left" }}>
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {batchLoading && (
                    <tr>
                      <td colSpan={8} style={{ ...grnTd, textAlign: "center", color: "var(--fg-muted)" }}>
                        Loading batches…
                      </td>
                    </tr>
                  )}
                  {!batchLoading && batchOptions.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ ...grnTd, textAlign: "center", color: "var(--fg-muted)" }}>
                        No existing batches — this will create a new one.
                      </td>
                    </tr>
                  )}
                  {!batchLoading &&
                    batchOptions.map((lot) => (
                    <tr
                      key={lot.id}
                      onClick={() => onPickBatch(lot)}
                      style={{ cursor: "pointer", background: entry.batch === lot.batch ? "var(--ink-50)" : "transparent" }}
                    >
                      <td style={{ ...grnTd, fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--fg-strong)" }}>
                        {lot.batch}
                      </td>
                      <td style={{ ...grnTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                        {cbFmtINR(lot.purchase)}
                      </td>
                      <td style={{ ...grnTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{cbFmtINR(lot.sale)}</td>
                      <td style={{ ...grnTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{cbFmtNum(lot.qtyA)}</td>
                      <td style={{ ...grnTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{cbFmtINR(lot.mrp)}</td>
                      <td style={{ ...grnTd, fontFamily: "var(--font-mono)" }}>{lot.exp}</td>
                      <td style={{ ...grnTd, fontFamily: "var(--font-mono)", color: "var(--fg-muted)" }}>{fmtGrnDate(lot.date) || lot.date}</td>
                      <td style={grnTd}>
                        <Pill variant="neutral">{lot.wh}</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Line items table */}
      <div style={{ background: "var(--bg-panel)", border: "1px solid var(--border-default)", borderRadius: 6, overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-subtle)",
          }}
        >
          <Eyebrow>Line items · {lines.length}</Eyebrow>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
              {pricing
                ? "Pricing…"
                : priceErr
                ? "Pricing unavailable"
                : `${cbFmtNum(totals.qty)} units · Tax ${cbFmtINR(totals.tax)} · Total ${cbFmtINR(
                    totals.amount
                  )}`}
            </span>
            <button
              onClick={() => setConfirmClearLines(true)}
              disabled={lines.length === 0}
              style={{
                border: 0,
                background: "transparent",
                color: lines.length ? "var(--red-700)" : "var(--fg-subtle, var(--fg-muted))",
                fontSize: 11,
                fontWeight: 600,
                cursor: lines.length ? "pointer" : "not-allowed",
              }}
            >
              Reset all
            </button>
          </div>
        </div>

        {confirmClearLines && (
          <ConfirmDialog
            title={`Remove all ${lines.length} line${lines.length !== 1 ? "s" : ""}?`}
            message="Every product added to this GRN will be removed, including bulk-imported rows. The purchase details above are kept. This cannot be undone."
            confirmLabel="Remove all lines"
            onConfirm={clearLines}
            onCancel={() => setConfirmClearLines(false)}
          />
        )}
        {priceErr && (
          <div
            style={{
              padding: "8px 14px",
              fontSize: 12,
              color: "var(--red-700)",
              background: "var(--red-50)",
              borderBottom: "1px solid var(--border-default)",
            }}
          >
            {priceErr} — totals shown are provisional; posting is disabled until pricing succeeds.
          </div>
        )}
        {/* Racks are optional on a receipt, so a failed load is a warning rather
            than a block: the goods still get received, just unracked. */}
        {racksErr && (
          <div
            style={{
              padding: "8px 14px",
              fontSize: 12,
              color: "var(--amber-700, var(--red-700))",
              background: "var(--amber-50, var(--bg-subtle))",
              borderBottom: "1px solid var(--border-default)",
            }}
          >
            {racksErr} — lines can still be received without a rack.
          </div>
        )}
        {!priceErr && warnings?.length > 0 && (
          <div
            style={{
              padding: "8px 14px",
              fontSize: 12,
              color: "var(--amber-700, var(--red-700))",
              background: "var(--amber-50, var(--bg-subtle))",
              borderBottom: "1px solid var(--border-default)",
            }}
          >
            {warnings.map((w, i) => (
              <div key={i}>⚠ {w.message || w.code}</div>
            ))}
          </div>
        )}
        <div style={{ overflowX: "auto" }} className="cb-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 1320 }}>
            <thead>
              <tr style={{ background: "var(--bg-subtle)" }}>
                {[
                  "Edit",
                  "Del",
                  "Product code",
                  "Product name",
                  "Batch",
                  "Qty",
                  "Free",
                  "Packing",
                  "Unit pack",
                  "Expiry",
                  "Scheme amt",
                  "Disc %",
                  "Disc amt",
                  "MRP",
                  "Purch price",
                  "Margin",
                  "HSN",
                  "GST",
                  "SGST",
                  "CGST",
                  "Rack",
                ].map((h, i) => (
                  <th key={h} style={{ ...grnTh, textAlign: i > 4 ? "right" : "left" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr>
                  <td colSpan={20} style={{ ...grnTd, textAlign: "center", color: "var(--fg-muted)", padding: 20 }}>
                    No products yet — add the first line below.
                  </td>
                </tr>
              )}
              {lines.map((l, i) => {
                /* Prefer backend-authoritative money; fall back to local math
                   only for display while pricing is loading (never for posting). */
                const pl = pricedByRef[String(i)];
                const fb = lineCalc(l);
                const c = pl
                  ? {
                      qty: num(l.quantity),
                      pp: num(l.purchasePrice),
                      sp: num(l.mrp),
                      discAmt: num(pl.discountAmount),
                      /* Backend echoes the resolved scheme (it derives the % when
                         a flat amount was sent), so prefer it over local math. */
                      schemeAmt: num(pl.schemeDiscountAmount),
                      gross: num(pl.gross),
                      taxable: num(pl.taxableAmount),
                      tax: round2(num(pl.cgstAmount) + num(pl.sgstAmount) + num(pl.igstAmount)),
                      sgst: num(pl.sgstAmount),
                      cgst: num(pl.cgstAmount),
                      margin: pl.margin != null ? num(pl.margin) : fb.margin,
                      amount: num(pl.lineTotal),
                    }
                  : fb;
                const marginPct = purchaseMarginPct(l, c.taxable);
                return (
                  <tr key={i} style={{ background: editIdx === i ? "var(--ink-50)" : "transparent" }}>
                    <td style={{ ...grnTd, textAlign: "center" }}>
                      <button
                        onClick={() => editLine(i)}
                        title="Edit line"
                        style={iconBtn("var(--ink-600)")}
                      >
                        <IconReceipt size={13} />
                      </button>
                    </td>
                    <td style={{ ...grnTd, textAlign: "center" }}>
                      <button
                        onClick={() => setConfirmDel(i)}
                        title="Delete line"
                        style={iconBtn("var(--red-700)")}
                      >
                        <IconX size={13} />
                      </button>
                    </td>
                    <td
                      style={{
                        ...grnTd,
                        fontFamily: "var(--font-mono)",
                        color: l.productCode ? undefined : "var(--red-700)",
                        fontWeight: l.productCode ? undefined : 600,
                      }}
                      title={l.productCode ? undefined : "No product code — edit this line and pick the product"}
                    >
                      {l.productCode || "— unmapped —"}
                    </td>
                    <td style={{ ...grnTd, fontWeight: 600, color: "var(--fg-strong)" }}>{l.productName}</td>
                    <td style={{ ...grnTd, fontFamily: "var(--font-mono)" }}>{l.batch}</td>
                    <NumTd v={c.qty} />
                    <NumTd v={num(l.free)} />
                    <td style={{ ...grnTd, fontFamily: "var(--font-mono)", textAlign: "right" }}>
                      {l.packing || "—"}
                    </td>
                    <td
                      style={{
                        ...grnTd,
                        fontFamily: "var(--font-mono)",
                        textAlign: "right",
                        color: isValidUnitPack(l.unit) ? undefined : "var(--red-700)",
                        fontWeight: isValidUnitPack(l.unit) ? undefined : 600,
                      }}
                      title={isValidUnitPack(l.unit) ? undefined : "Missing / invalid unit pack (whole number, 1 or more)"}
                    >
                      {l.unit || "—"}
                    </td>
                    <td
                      style={{
                        ...grnTd,
                        fontFamily: "var(--font-mono)",
                        textAlign: "right",
                        color: isValidExpiry(l.expiry) ? undefined : "var(--red-700)",
                        fontWeight: isValidExpiry(l.expiry) ? undefined : 600,
                      }}
                      title={isValidExpiry(l.expiry) ? undefined : "Invalid / missing expiry (MM/YY)"}
                    >
                      {l.expiry || "—"}
                    </td>
                    <NumTd v={c.schemeAmt} money />
                    <NumTd v={num(l.discountPct)} suffix="%" />
                    <NumTd v={c.discAmt} money />
                    <NumTd v={num(l.mrp)} money />
                    <NumTd v={c.pp} money />
                    <td
                      style={{
                        ...grnTd,
                        textAlign: "right",
                        fontFamily: "var(--font-mono)",
                        color:
                          marginPct == null
                            ? "var(--fg-muted)"
                            : marginPct >= 0
                              ? "var(--green-700)"
                              : "var(--red-700)",
                        fontWeight: 600,
                      }}
                      title={marginPct == null ? "Needs MRP and quantity" : "Margin on MRP, after scheme, discount and GST"}
                    >
                      {marginPct == null ? "—" : `${trunc2(marginPct)}%`}
                    </td>
                    <td
                      style={{
                        ...grnTd,
                        fontFamily: "var(--font-mono)",
                        textAlign: "right",
                        color: isValidHsn(l.taxCategory) ? undefined : "var(--red-700)",
                        fontWeight: isValidHsn(l.taxCategory) ? undefined : 600,
                      }}
                      title={isValidHsn(l.taxCategory) ? undefined : `Invalid / missing HSN (${HSN_RULE_TEXT})`}
                    >
                      {l.taxCategory || "—"}
                    </td>
                    <td
                      style={{
                        ...grnTd,
                        fontFamily: "var(--font-mono)",
                        textAlign: "right",
                        color: isValidGst(l.taxPct) ? undefined : "var(--red-700)",
                        fontWeight: isValidGst(l.taxPct) ? undefined : 600,
                      }}
                      title={isValidGst(l.taxPct) ? undefined : "Invalid / missing GST slab (0, 5, 12, 18, 28)"}
                    >
                      {l.taxPct !== "" && l.taxPct != null ? `${cbFmtNum(num(l.taxPct))}%` : "—"}
                    </td>
                    <NumTd v={c.sgst} money />
                    <NumTd v={c.cgst} money />
                    <td style={{ ...grnTd, textAlign: "right" }}>
                      {/* Editable in place: the rack is where the stock is being
                          put away, decided at the shelf and not on the invoice.
                          Prefilled from the medicine's home shelf; a rack that is
                          not in the master yet can be created from the dropdown. */}
                      <RackPicker
                        value={l.rackId}
                        racks={racks}
                        fallbackLabel={l.rackCode}
                        width={96}
                        disabled={!warehouseId}
                        disabledHint="Select the receiving store first"
                        warehouseLabel={warehouseId}
                        onChange={(rackId, rack) => patchLine(i, { rackId, rackCode: rack?.code || "" })}
                        onCreate={createRack}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {confirmDel !== null && lines[confirmDel] && (
        <DeleteLineDialog
          line={lines[confirmDel]}
          index={confirmDel}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => {
            delLine(confirmDel);
            setConfirmDel(null);
          }}
        />
      )}
    </div>
  );
};

/* Confirm removing a line from the GRN. Deleting drops the batch, expiry and
   pricing the operator keyed off the supplier's invoice — none of which is
   recoverable from the table — and it moves the computed total away from the
   invoice amount, so the invoice no longer reconciles until the line is re-entered
   or the difference is explained. Worth a deliberate second click. */
const DeleteLineDialog = ({ line, index, onCancel, onConfirm }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 95,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <div onClick={onCancel} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
    <div
      role="dialog"
      aria-modal="true"
      className="cb-animate-in"
      style={{
        position: "relative",
        width: "min(460px, 94vw)",
        background: "var(--bg-panel)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        boxShadow: "0 24px 64px rgba(13,17,27,0.22)",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", gap: 12, padding: 18 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            background: "var(--red-50)",
            color: "var(--red-700)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconAlert size={18} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--fg-strong)" }}>
            Remove this line from the GRN?
          </div>
          <div style={{ fontSize: 12, color: "var(--fg-default)", marginTop: 6, lineHeight: 1.5 }}>
            Line {index + 1} —{" "}
            <strong style={{ color: "var(--fg-strong)" }}>
              {line.productName || line.productCode || "unnamed product"}
            </strong>
            {line.batch ? (
              <>
                , batch <span style={{ fontFamily: "var(--font-mono)" }}>{line.batch}</span>
              </>
            ) : null}
            {num(line.quantity) ? `, qty ${cbFmtNum(num(line.quantity))}` : ""}.
          </div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 8, lineHeight: 1.5 }}>
            Its batch, expiry and pricing are discarded and cannot be restored — the line has to be
            entered again from the supplier's invoice. The computed total will no longer match the
            invoice amount until it is.
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          padding: "12px 18px",
          borderTop: "1px solid var(--border-default)",
          background: "var(--bg-subtle)",
        }}
      >
        <Button onClick={onCancel}>Keep line</Button>
        <Button variant="danger" icon={<IconX size={14} />} onClick={onConfirm}>
          Remove line
        </Button>
      </div>
    </div>
  </div>
);

const iconBtn = (color) => ({
  border: 0,
  background: "transparent",
  cursor: "pointer",
  color,
  padding: 4,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const NumTd = ({ v, money, suffix }) => (
  <td style={{ ...grnTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>
    {money ? cbFmtINR(v) : cbFmtNum(v)}
    {suffix || ""}
  </td>
);

export default Grn;
